import {
  CalendarOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
  FileOutlined,
  FieldTimeOutlined,
  ImportOutlined,
  RightOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Radio, Space, Switch, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { ReactComponent as IconBetaStar } from 'assets/images/icons/iconBetaStar.svg';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { ReactComponent as IconAttachment } from 'assets/images/icons/iconTransactionAttachment.svg';
import ExcelLogo from 'assets/images/excel_small.png';
import ScanTransactionsImage from 'assets/images/scan-transactions.svg';
import UploadTransactionsImage from 'assets/images/upload-transactions.svg';
import { ActiveStepSpinner } from 'components/Custom/ActiveStepSpinner';
import { StyledCard } from 'components/Custom/card';
import { CardDescSpan, CardTitleLargeSpan } from 'components/Custom/spans';
import { AttachmentViewer } from 'components/Vat/Report/tables/transactions/components/AttachmentViewer/index';
import {
  RECEIPT_UPLOAD_ACCEPT,
  RECEIPT_UPLOAD_ACCEPT_WITH_CAMERA,
  partitionFilesByValidation,
  validateReceiptUploadFile,
} from 'utils/safe-upload';
import {
  getProgressFromUploadStatus,
  getUploadFileIndicatorState,
  isDeletedReceiptStatus,
  isUploadStatusError,
  isUploadStatusProcessing,
} from 'utils/uploadStatusHelpers';
import { CardInfo } from './card-info';

import { MessageStates } from 'containers/MessageBox';
import moment from 'moment';
import { turnDrawerOff, turnDrawerOn, turnMessageOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import type { DrawerData, MessageData } from 'store/app/types';
import { getDrawerData, getDrawerOpen } from 'store/app/selectors';
import { fetchChartOfAccountsForOrganisationRequest } from 'store/chart-of-accounts/actions';
import { getChartOfAccountsLoading } from 'store/chart-of-accounts/selectors';
import { getCommonCountries } from 'store/common/selectors';
import {
  clearReceiptUploadStatus,
  completeScanStepsRequest,
  fetchAllExcelTransactionsRequest,
  fetchExcelFilesMetadataRequest,
  fetchLastReceiptUploadStatusRequest,
  fetchOrganisationContactsRequest,
  uploadReceiptRequest,
} from 'store/organisation/actions';
import {
  ReceiptUploadStatus,
  ReceiptUploadStatusFile,
  UploadStatus,
  type ValidatedExcelTransaction,
} from 'store/organisation/models';
import {
  getLastReceiptUploadStatus,
  getLastUploadedReceiptsBatchId,
  getOrgOrganisations,
  getUploadInitFailed,
} from 'store/organisation/selectors';
import type { OrganisationState } from 'store/organisation/types';
import { getReceiptRulesForConnection } from 'store/receipt-rules/selectors';
import { fetchReturnTypesRequest } from 'store/returns/actions';
import { getReturnLoading } from 'store/returns/selectors';
import {
  getTaxRateTemplatesRequest,
  refreshConnectionTaxRatesRequest,
} from 'store/tax-rates/actions';
import {
  getTaxRatesForConnection,
  getTaxRatesGettingTaxes,
} from 'store/tax-rates/selectors';
import type { TransactionAttachment } from 'store/vat/models/transactionAttachments';
import { getLastFetchedTransactionAttachments } from 'store/vat/selectors';
import { AddChartOfAccountsForm } from './add-chart-of-accounts-form';
import DriveUploadHistory from './drive-upload-history';
import CaptureStatisticsHistory from './capture-statistics-history';
import { resolveOrganisationTaxRatesForSetupCheck } from './transactions-tab/entity-transaction-helpers';
import { ENTITY_TOOLBAR_MUTED_ICON_COLOR } from './transactions-tab/constants';
import {
  EntityTransactionsToolbarIconButton,
  entityTransactionsToolbarButtonStyle,
} from './transactions-tab/styles';

import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import type { ScopeTourStep } from 'components/Custom/ScopeTour';
import {
  SCOPE_TOUR_STEP_TITLE_CLASS,
  ScopeTour,
} from 'components/Custom/ScopeTour';
import SseNotifications from 'components/ServerSentEvents';
import {
  RECEIPTS_BEFORE_SCAN_CHECKLIST_DONE_KEY_PREFIX,
  RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX,
  RECEIPTS_GROUP_BY_TAX_CODE_KEY_PREFIX,
  RECEIPTS_MAP_ITEMS_KEY_PREFIX,
  RECEIPTS_MULTI_CURRENCY_MODE_KEY_PREFIX,
  RECEIPTS_ONBOARDING_TOUR_SHOWN_KEY,
} from 'shared/constants/transactionsTourKeys';
import { Provider } from 'store/api/enums';
import { getAuthUser } from 'store/auth/selectors';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { AddNewTaxesForm } from './add-new-taxes-form';
import {
  EntityProfileDrawerContent,
  hasEntityProfileSection,
} from './EntityProfileDrawerContent';
import { useReceiptsScanPrerequisitesMet } from './receipts-scan-prerequisites';
import { getRulesForApplicationConnectionRequest } from 'store/receipt-rules/actions';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { canUseDesktopWebcam, isPwaScanCameraRequested, stripPwaScanCameraParam } from 'utils/pwa-nav';
import { WebcamCaptureModal } from './WebcamCaptureModal';
import { PwaCapturePage } from 'pwa/components/PwaCapturePage';

export type UploadTransactionsPanelVariant = 'excel' | 'receipts' | '3rdParty';

const MAX_SCAN_FILES = 10;

/** Excel template download URL: staging (react.generate.tax) or production (api.generate.tax) from API_URL. */
const EXCEL_TEMPLATE_DOWNLOAD_URL =
  typeof process.env.API_URL === 'string' && process.env.API_URL
    ? `${new URL(process.env.API_URL).origin}/api/auth/excel/download/template`
    : 'https://api.generate.tax/api/auth/excel/download/template';

/** Support article URL for Excel / CSV upload requirements. */
const SUPPORT_ARTICLE_URL =
  'https://help.generate.tax/portal/en/kb/articles/import-data-using-excel-csv';

// Project colors: gold (ScopeButton dark-orange), success/error (notifications)
const COLORS = {
  gold: 'var(--btn-accent-bg, #FFBC3E)',
  success: 'var(--color-status-success, var(--color-primary, #41aaaf))',
  successBg: 'var(--color-status-success-bg, var(--color-layout-bg, #E9F6F7))',
  error: 'var(--color-status-danger, #D13131)',
  errorBg: 'var(--color-status-danger-bg, #FCE7E7)',
} as const;

const UploadedFilesBox = styled.div<{ $pwa?: boolean }>`
  flex: ${(p) => (p.$pwa ? '0 0 auto' : '1.2')};
  min-width: 0;
  overflow: hidden;
  border-radius: ${(p) => (p.$pwa ? '8px' : '10px')};
  border: 1px solid var(--color-border, #dbeae3);
  background: var(--color-surface, #f3f7f8);
  padding: ${(p) => (p.$pwa ? '10px' : '20px')};
`;

const ScanningPreferencesBox = styled.div<{
  $disabled?: boolean;
  $variant?: 'boxed' | 'drawer';
}>`
  flex: 1;
  min-width: ${(p) => (p.$variant === 'drawer' ? '0' : 'min(100%, 300px)')};
  max-width: ${(p) => (p.$variant === 'drawer' ? 'none' : '420px')};
  width: ${(p) => (p.$variant === 'drawer' ? '100%' : 'auto')};
  border-radius: ${(p) => (p.$variant === 'drawer' ? '0' : '10px')};
  border: ${(p) =>
    p.$variant === 'drawer'
      ? 'none'
      : '1px solid var(--color-border, #dbeae3)'};
  background: ${(p) =>
    p.$variant === 'drawer'
      ? 'transparent'
      : 'var(--color-surface, #f3f7f8)'};
  padding: ${(p) => (p.$variant === 'drawer' ? '0' : '20px')};
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};
`;

const BeforeYouScanLayout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: clamp(16px, 3vw, 32px);
  padding: 0 24px;
`;

const BeforeYouScanSteps = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  flex-shrink: 0;
  padding-bottom: 24px;
  max-width: fit-content;
`;

const BeforeYouScanIllustration = styled.img`
  flex: 0 1 300px;
  width: 100%;
  max-width: 300px;
  min-width: 100px;
  height: auto;
  object-fit: contain;
  align-self: center;
`;

const BeforeYouScanStepLabel = styled.span`
  color: var(--color-text-dark, #042a2a);
  font-size: 14px;
  min-width: 0;
`;

const BeforeYouScanStepContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;

  & > *:last-child {
    flex-shrink: 0;
  }
`;

const ReceiptUploadLayout = styled.div<{
  $matchColumnHeights?: boolean;
  $pwa?: boolean;
}>`
  display: flex;
  flex-direction: ${(p) => (p.$pwa ? 'column' : 'row')};
  align-items: ${(p) =>
    p.$pwa ? 'stretch' : p.$matchColumnHeights ? 'stretch' : 'flex-start'};
  gap: ${(p) => (p.$pwa ? '12px' : '20px')};
  width: 100%;
  margin-bottom: ${(p) => (p.$pwa ? '8px' : '12px')};

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

const ReceiptDropZone = styled.div<{
  $canUpload: boolean;
  $isDragOver: boolean;
  $pwa?: boolean;
}>`
  flex: 1.2;
  min-width: 0;
  border: 2px dashed
    ${(props) =>
      props.$canUpload && props.$isDragOver
        ? 'var(--btn-accent-bg, #ffbc3e)'
        : 'var(--color-border, #dbeae3)'};
  border-radius: 8px;
  padding: 24px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  background-color: ${(props) =>
    props.$canUpload && props.$isDragOver
      ? 'var(--color-status-warning-bg, #fff5e2)'
      : props.$pwa
        ? 'transparent'
        : 'var(--color-surface-raised, #fafafa)'};
  cursor: ${(props) => (props.$canUpload ? 'pointer' : 'not-allowed')};
  opacity: ${(props) => (props.$canUpload ? 1 : 0.7)};
  color: var(--color-text-base, #2e2e2e);
`;

const ScanningPreferencesTitle = styled.h3`
  margin: 0 0 20px;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: var(--color-text-dark, #042a2a);
`;

const ScanningPreferenceRow = styled.div`
  &:not(:last-child) {
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--color-border, #dbeae3);
  }
`;

const ScanningPreferenceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const ScanningPreferenceText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScanningPreferenceLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-text-dark, #042a2a);
`;

const ScanningPreferenceLabelWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ExtractionRulesPreferenceIcon = styled(IconFuzzyMapping)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--color-primary, #41aaaf);
`;

const ScanningPreferenceDescription = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-muted, #5a6a6a);
`;

const MultiCurrencyToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
  }
`;

const MultiCurrencyPanel = styled.div`
  margin-top: 14px;
  padding: 14px 16px;
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 8px;
`;

const MultiCurrencyIntro = styled.p`
  margin: 0 0 16px;
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-dark, #042a2a);
`;

const MultiCurrencyRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;

  .ant-radio-wrapper {
    align-items: flex-start;
    margin-inline-end: 0;
    width: 100%;
  }

  .ant-radio {
    margin-top: 2px;
  }
`;

const MultiCurrencyOptionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-text-dark, #042a2a);
`;

const MultiCurrencyOptionDescription = styled.div`
  font-size: 12px;
  line-height: 18px;
  color: var(--color-text-muted, #5a6a6a);
  margin-top: 2px;
`;

export type ReceiptMultiCurrencyMode = 'automatic_conversion' | 'stop_upload';

const LEGACY_RECEIPTS_MULTI_CURRENCY_MODE_KEY_PREFIX =
  'receipts-multi-currency-mode';
const RECEIPTS_MULTI_CURRENCY_CONVERT_VALUE = 'CONVERT';
const RECEIPTS_MULTI_CURRENCY_STOP_VALUE = 'STOP';

function serializeMultiCurrencyModePreference(
  mode: ReceiptMultiCurrencyMode
): string {
  return mode === 'stop_upload'
    ? RECEIPTS_MULTI_CURRENCY_STOP_VALUE
    : RECEIPTS_MULTI_CURRENCY_CONVERT_VALUE;
}

function parseMultiCurrencyModePreference(
  saved: unknown
): ReceiptMultiCurrencyMode | undefined {
  if (
    saved === RECEIPTS_MULTI_CURRENCY_CONVERT_VALUE ||
    saved === 'automatic_conversion'
  ) {
    return 'automatic_conversion';
  }
  if (saved === RECEIPTS_MULTI_CURRENCY_STOP_VALUE || saved === 'stop_upload') {
    return 'stop_upload';
  }
  return undefined;
}

function getDefaultMultiCurrencyMode(
  providerName?: string | null
): ReceiptMultiCurrencyMode {
  return entitySupportsMultiCurrencyStopUpload(providerName)
    ? 'stop_upload'
    : 'automatic_conversion';
}

const MULTI_CURRENCY_STOP_UPLOAD_PROVIDERS = new Set<string>([
  Provider.XERO,
  Provider.QUICKBOOKS,
  Provider.ZOHO,
]);

export function entitySupportsMultiCurrencyStopUpload(
  providerName?: string | null
): boolean {
  const providerKey = providerName?.toLowerCase().trim();
  return !!providerKey && MULTI_CURRENCY_STOP_UPLOAD_PROVIDERS.has(providerKey);
}

export function entitySupportsMapItems(providerName?: string | null): boolean {
  return entitySupportsMultiCurrencyStopUpload(providerName);
}

const UploadedFilesTitleRow = styled.div<{ $pwa?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => (p.$pwa ? '8px' : '24px')};
`;

const UploadedFilesTitle = styled.span<{ $pwa?: boolean }>`
  font-size: ${(p) => (p.$pwa ? '14px' : '16px')};
  font-weight: 500;
  line-height: ${(p) => (p.$pwa ? '20px' : '24px')};
  color: var(--color-text-dark, #042a2a);
`;

const UploadedFilesProcessingHeader = styled.div<{ $pwa?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$pwa ? '8px' : '12px')};
  margin-bottom: ${(p) => (p.$pwa ? '8px' : '20px')};
`;

const UploadedFilesProcessingIcon = styled.div<{ $pwa?: boolean }>`
  width: ${(p) => (p.$pwa ? '28px' : '40px')};
  height: ${(p) => (p.$pwa ? '28px' : '40px')};
  border-radius: ${(p) => (p.$pwa ? '8px' : '10px')};
  background: var(--color-status-warning-bg, #fff5e2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UploadedFilesProcessingTitle = styled.div<{ $pwa?: boolean }>`
  font-size: ${(p) => (p.$pwa ? '13px' : '16px')};
  font-weight: 600;
  line-height: ${(p) => (p.$pwa ? '18px' : '24px')};
  color: var(--color-text-dark, #042a2a);
`;

const UploadedFilesProcessingSubtitle = styled.div<{ $pwa?: boolean }>`
  font-size: ${(p) => (p.$pwa ? '11px' : '13px')};
  line-height: ${(p) => (p.$pwa ? '16px' : '20px')};
  color: var(--color-text-muted, #5a6a6a);
  margin-top: ${(p) => (p.$pwa ? '0' : '2px')};
`;

const UploadedFilesStepsList = styled.div<{ $pwa?: boolean }>`
  list-style: none;
  margin: 0 0 ${(p) => (p.$pwa ? '8px' : '24px')};
  padding: 0;
  display: flex;
  flex-direction: ${(p) => (p.$pwa ? 'column' : 'row')};
  flex-wrap: ${(p) => (p.$pwa ? 'nowrap' : 'wrap')};
  align-items: ${(p) => (p.$pwa ? 'stretch' : 'center')};
  gap: ${(p) => (p.$pwa ? '6px' : '24px 32px')};
`;

const UploadedFilesStepRow = styled.div<{
  $state: 'pending' | 'active' | 'complete';
  $pwa?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${(p) => (p.$pwa ? '6px' : '10px')};
  min-width: 0;
  width: ${(p) => (p.$pwa ? '100%' : 'auto')};
  color: ${(props) =>
    props.$state === 'pending'
      ? 'var(--color-text-muted, #5a6a6a)'
      : props.$state === 'active'
      ? 'var(--color-text-dark, #042a2a)'
      : '#41aaaf'};
  font-size: ${(p) => (p.$pwa ? '11px' : '12px')};
  line-height: ${(p) => (p.$pwa ? '16px' : '20px')};
  font-weight: ${(props) => (props.$state === 'active' ? 600 : 400)};
  transition: color 0.25s ease;
`;

const StepIndicator = styled.div<{
  $state: 'pending' | 'active' | 'complete' | 'error';
  $pwa?: boolean;
}>`
  width: ${(p) => (p.$pwa ? '22px' : '28px')};
  height: ${(p) => (p.$pwa ? '22px' : '28px')};
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${(p) => (p.$pwa ? '10px' : '12px')};
  font-weight: 700;
  border: 2px solid
    ${(props) =>
      props.$state === 'error'
        ? 'var(--color-status-danger, #d13131)'
        : props.$state === 'complete'
        ? 'var(--color-primary, #41aaaf)'
        : props.$state === 'active'
        ? 'var(--btn-accent-bg, #ffbc3e)'
        : 'var(--color-border, #dbeae3)'};
  background: ${(props) =>
    props.$state === 'error'
      ? 'var(--color-status-danger-bg, #fce7e7)'
      : props.$state === 'complete'
      ? 'var(--color-status-success-bg, var(--color-layout-bg, #e9f6f7))'
      : props.$state === 'active'
      ? 'var(--color-status-warning-bg, #fff5e2)'
      : 'var(--color-surface, #f3f7f8)'};
  color: ${(props) =>
    props.$state === 'error'
      ? 'var(--color-status-danger, #d13131)'
      : props.$state === 'complete'
      ? 'var(--color-status-success, var(--color-primary, #41aaaf))'
      : props.$state === 'active'
      ? 'var(--color-text-base, #2e2e2e)'
      : 'var(--color-text-muted, #5a6a6a)'};
`;

const FileStatusCell = styled.div<{ $pwa?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${(p) => (p.$pwa ? '6px' : '10px')};
  flex: ${(p) => (p.$pwa ? 'none' : '1 1 0')};
  min-width: 0;
  max-width: 100%;
  width: ${(p) => (p.$pwa ? '100%' : 'auto')};
  box-sizing: border-box;
  overflow: hidden;
  /* Indent under filename text (past attachment icon) so statuses share one left edge */
  padding-left: ${(p) => (p.$pwa ? '30px' : '0')};
`;

const FileStatusLabel = styled.span<{
  $done?: boolean;
  $error?: boolean;
  $pwa?: boolean;
}>`
  font-size: ${(p) => (p.$pwa ? '11px' : '13px')};
  line-height: ${(p) => (p.$pwa ? '15px' : '20px')};
  min-width: 0;
  flex: 1 1 auto;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: ${(props) =>
    props.$error
      ? 'var(--color-status-danger, #d13131)'
      : props.$done
        ? 'var(--color-status-success, var(--color-primary, #41aaaf))'
        : 'var(--color-text-base, #2e2e2e)'};
  font-weight: ${(props) => (props.$done || props.$error ? 500 : 400)};
`;

const UploadedFilesList = styled.div<{ $pwa?: boolean }>`
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 8px;
  padding: ${(p) => (p.$pwa ? '0 8px' : '4px 16px')};
  margin-bottom: ${(p) => (p.$pwa ? '8px' : '20px')};
  min-width: 0;
  overflow: hidden;
`;

const UploadedFileRow = styled.div<{ $pwa?: boolean }>`
  display: flex;
  align-items: ${(p) => (p.$pwa ? 'stretch' : 'flex-start')};
  flex-direction: ${(p) => (p.$pwa ? 'column' : 'row')};
  gap: ${(p) => (p.$pwa ? '4px' : '16px')};
  flex-wrap: ${(p) => (p.$pwa ? 'nowrap' : 'wrap')};
  min-width: 0;
  padding: ${(p) => (p.$pwa ? '8px 0' : '10px 0')};
  border-bottom: 1px solid var(--color-border, #eef4f4);

  &:last-child {
    border-bottom: none;
  }

  ${(p) =>
    p.$pwa
      ? `
    & > span:first-child {
      width: 100%;
      min-width: 0;
    }

    & > span:first-child > span:last-child {
      font-size: 11px;
      line-height: 15px;
    }

    & > button {
      align-self: flex-end;
      order: 3;
    }
  `
      : ''}
`;

const UploadedFilesFooter = styled.div<{ $pwa?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${(p) => (p.$pwa ? '6px' : '12px')};
  align-items: ${(p) => (p.$pwa ? 'stretch' : 'center')};
  justify-content: space-between;
  flex-direction: ${(p) => (p.$pwa ? 'column' : 'row')};

  ${(p) =>
    p.$pwa
      ? `
    & .ant-space {
      width: 100%;
    }
    & .ant-space-item {
      flex: 1 1 auto;
    }
    & button {
      width: 100%;
    }
  `
      : ''}
`;

const UploadedFilesSuccess = styled.div<{ $pwa?: boolean }>`
  margin-top: ${(p) => (p.$pwa ? '0' : '4px')};
  margin-bottom: ${(p) => (p.$pwa ? '8px' : '16px')};
  padding: ${(p) => (p.$pwa ? '8px 10px' : '12px 14px')};
  border-radius: 8px;
  background: var(--color-layout-bg, #e9f6f7);
  border: 1px solid var(--color-primary, #41aaaf);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${(p) => (p.$pwa ? '12px' : '13px')};
  font-weight: 600;
  line-height: ${(p) => (p.$pwa ? '16px' : '20px')};
  color: #41aaaf;
`;

const renderStepIndicatorContent = (
  state: 'pending' | 'active' | 'complete' | 'error',
  stepNumber?: number
) => {
  if (state === 'complete') {
    return <CheckOutlined style={{ fontSize: 13 }} />;
  }
  if (state === 'error') {
    return '!';
  }
  if (state === 'active') {
    return <ActiveStepSpinner />;
  }
  return stepNumber ?? null;
};

export type ScanStage =
  | 'uploading_file'
  | 'processing_ai'
  | 'converting_and_saving'
  | 'done'
  | 'error';

const getScanStageLabels = (
  t: (key: string) => string
): Record<ScanStage, string> => ({
  uploading_file: t('organisation.scan-stage-uploading-file'),
  processing_ai: t('organisation.scan-stage-processing-ai'),
  converting_and_saving: t('organisation.scan-stage-converting-and-saving'),
  done: t('common.done'),
  error: t('common.error'),
});

const getScanStageIndicatorState = (
  stage: ScanStage
): 'pending' | 'active' | 'complete' | 'error' => {
  if (stage === 'done') return 'complete';
  if (stage === 'error') return 'error';
  return 'active';
};

const normalizeUploadedFileName = (name: string): string =>
  name.trim().toLowerCase();

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const base64 = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const buildTransactionAttachmentFromFile = async (
  file: File
): Promise<TransactionAttachment> => ({
  attachmentID: `local-scan-${normalizeUploadedFileName(file.name)}`,
  fileName: file.name,
  fileContent: await readFileAsBase64(file),
});

export interface ScanFileItem {
  id: string;
  file?: File;
  name: string;
  stage: ScanStage;
  removable?: boolean;
}

const sameReceiptId = (left: unknown, right: unknown): boolean =>
  String(left ?? '').trim() === String(right ?? '').trim();

const unwrapReceiptUploadStatus = (
  raw: unknown
): ReceiptUploadStatus | null => {
  if (!raw || typeof raw !== 'object') return null;
  const asStatus = raw as ReceiptUploadStatus;
  if (Array.isArray(asStatus.files)) return asStatus;
  const nested = (raw as { status?: ReceiptUploadStatus }).status;
  if (nested && typeof nested === 'object' && Array.isArray(nested.files)) {
    return nested;
  }
  return null;
};

const receiptUploadStatusSyncKey = (
  status: ReceiptUploadStatus | null
): string => {
  if (!status) return '';
  return `${status.status}-${status.files?.length ?? 0}-${
    status.files?.[0]?.importId ?? ''
  }-${(status.files ?? [])
    .map(
      (file) =>
        `${file.status ?? ''}:${file.errorReason ?? ''}:${file.uploadDate ?? ''}:${file.fileName ?? ''}`
    )
    .join('|')}`;
};

const scanStageFromReceiptStatus = (status: unknown): ScanStage => {
  if (isUploadStatusError(status)) return 'error';
  if (isUploadStatusProcessing(status)) return 'processing_ai';
  return 'done';
};

const markReceiptFilesTerminal = (
  files: ReceiptUploadStatusFile[]
): ReceiptUploadStatusFile[] =>
  files.map((file) =>
    isUploadStatusProcessing(file.status)
      ? { ...file, status: UploadStatus.ACTIVE }
      : file
  );

const receiptFilesFromScan = (
  files: ScanFileItem[]
): ReceiptUploadStatusFile[] =>
  files.map((file) => ({
    importId: file.id,
    fileName: file.name,
    uploadDate: '',
    totalRows: 0,
    fileType: '',
    accountDetailsId: '',
    status: file.stage === 'error' ? UploadStatus.AI_ERROR : UploadStatus.ACTIVE,
    user: null,
    errorReason: '',
  }));

interface UploadTransactionsPanelProps {
  variant?: UploadTransactionsPanelVariant;
  organisationId?: string;
  organisation?: any;
  onShowCsvInfo: () => void;
  onShowExcelInfo: () => void;
  onUploadTransactions: () => void;
  onGoToTransactionsTab?: () => void;
  onFilesUploaded?: () => void;
  /** Optional: notify parent when receipts batch processing starts/ends. */
  onReceiptsBatchStatusChange?: (inProgress: boolean) => void;
  /** Optional: when first receipt batch completes, call with importId so parent can e.g. show the Transactions tour. */
  onReceiptsBatchComplete?: (importId: string) => void;
  /** For receipts: true when there is at least 1 transaction in the Transactions tab (scanned receipt exists). */
  hasTransactions?: boolean;
  /** For Excel: paginated transactions data (used for most recent transaction date in header). */
  allExcelTransactionsPage?: OrganisationState['allExcelTransactionsPage'];
  /** Optional control(s) anchored to the top-right of the before-you-scan checklist card. */
  firstBoxTopRightExtras?: React.ReactNode;
  /** Optional control(s) anchored to the top-right of the main upload / scan documents card. */
  uploadDocumentsTopRightExtras?: React.ReactNode;
  /** Opens extraction rules management for this entity (receipts capture). */
  onOpenInterpretationRules?: () => void;
  /** Optional: one-shot optimistic placeholder for a receipt re-scan triggered from Transactions. */
  optimisticRescanFileName?: string | null;
  /** Called after the optimistic re-scan placeholder has been consumed locally. */
  onOptimisticRescanShown?: () => void;
}

export const UploadTransactionsPanel: React.FC<
  UploadTransactionsPanelProps
> = ({
  variant = 'excel',
  organisationId,
  organisation,
  onShowCsvInfo,
  onShowExcelInfo,
  onUploadTransactions,
  onGoToTransactionsTab,
  onFilesUploaded,
  onReceiptsBatchStatusChange,
  onReceiptsBatchComplete,
  hasTransactions = false,
  allExcelTransactionsPage,
  firstBoxTopRightExtras,
  uploadDocumentsTopRightExtras,
  optimisticRescanFileName,
  onOptimisticRescanShown,
  onOpenInterpretationRules,
}) => {
  const isReceipts = variant === 'receipts' || variant === '3rdParty';
  const pwaMobileNav = usePwaMobileNavEnabled();
  const desktopWebcamAvailable = React.useMemo(() => canUseDesktopWebcam(), []);
  const [webcamOpen, setWebcamOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const scanCameraPendingRef = React.useRef(
    isPwaScanCameraRequested(location.search)
  );

  React.useEffect(() => {
    if (isPwaScanCameraRequested(location.search)) {
      scanCameraPendingRef.current = true;
    }
  }, [location.search]);
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const scanStageLabels = React.useMemo(() => getScanStageLabels(t), [t]);

  const organisations = useSelector(getOrgOrganisations);
  const resolvedOrganisation = React.useMemo(() => {
    if (organisationId) {
      const fromStore = organisations?.find((o) => o.uuid === organisationId);
      if (fromStore) return fromStore;
    }
    return organisation;
  }, [organisations, organisationId, organisation]);

  const gettingTaxes = useSelector(getTaxRatesGettingTaxes);
  const taxRatesForConnectionSelector = React.useMemo(
    () => getTaxRatesForConnection(resolvedOrganisation?.connectionId),
    [resolvedOrganisation?.connectionId]
  );
  const taxRatesForConnection = useSelector(taxRatesForConnectionSelector);
  const returnLoading = useSelector(getReturnLoading);
  const receiptRules = useSelector(getReceiptRulesForConnection);
  const extractionRulesCount = receiptRules?.length ?? 0;
  const gettingChartOfAccounts = useSelector(getChartOfAccountsLoading);
  const countriesRaw = useSelector(getCommonCountries);
  const countries = Array.isArray(countriesRaw) ? countriesRaw : [];
  const drawerOpen = useSelector(getDrawerOpen);
  const drawerData = useSelector(getDrawerData);
  const scanningPrefsDrawerOpen =
    Boolean(drawerOpen) &&
    drawerData?.type === DrawerType.SCANNING_PREFERENCES;

  // Redux state for receipt upload status (receipts only) – live update from API + SSE
  const batchId = useSelector(getLastUploadedReceiptsBatchId);
  const lastReceiptUploadStatus = useSelector(getLastReceiptUploadStatus);
  const lastFetchedAttachments = useSelector(
    getLastFetchedTransactionAttachments
  );

  const [uploadPanelAttachmentPreview, setUploadPanelAttachmentPreview] =
    React.useState<{
      fileName: string;
      attachments: TransactionAttachment[];
    } | null>(null);
  const [uploadPanelAttachmentLoading, setUploadPanelAttachmentLoading] =
    React.useState(false);
  const localAttachmentCacheRef = React.useRef<
    Map<string, TransactionAttachment>
  >(new Map());
  const [currentStatus, setCurrentStatus] =
    React.useState<ReceiptUploadStatus | null>(null);
  const [scanFiles, setScanFiles] = React.useState<ScanFileItem[]>([]);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [batchAllProcessed, setBatchAllProcessed] = React.useState(false);
  const [coaSelected, setCoaSelected] = React.useState(false);
  /** Scroll target for the scan / progress card (below the before-you-scan checklist). */
  const scanProgressCardRef = React.useRef<HTMLDivElement | null>(null);

  /** When true, backend should group extracted data by tax code. Sent with upload when backend supports it. Persisted to user preferences. */
  const [groupByTaxCode, setGroupByTaxCode] = React.useState(true);
  /** When true, receipt scans map extracted line items. Persisted to user preferences. */
  const [mapItems, setMapItems] = React.useState(false);
  const [multiCurrencyOpen, setMultiCurrencyOpen] = React.useState(false);
  const [multiCurrencyMode, setMultiCurrencyMode] =
    React.useState<ReceiptMultiCurrencyMode>('automatic_conversion');

  const preferencesHook = usePreferences();
  const uploadInitFailed = useSelector(getUploadInitFailed);

  // When the initial upload fails, reset the local scan state so the user
  // sees the default drag-and-drop panel again instead of a stuck loading view.
  React.useEffect(() => {
    if (!uploadInitFailed) return;
    setScanFiles([]);
    setCurrentStatus(null);
  }, [uploadInitFailed]);
  const currentUser = useSelector(getAuthUser);
  const supportsStopUploadMultiCurrency = React.useMemo(
    () => entitySupportsMultiCurrencyStopUpload(organisation?.providerName),
    [organisation?.providerName]
  );
  const supportsMapItems = React.useMemo(
    () => entitySupportsMapItems(resolvedOrganisation?.providerName),
    [resolvedOrganisation?.providerName]
  );
  const coaSkippedPrefKey =
    isReceipts && organisationId
      ? `${RECEIPTS_BEFORE_SCAN_COA_SKIPPED_KEY_PREFIX}-${organisationId}`
      : null;
  const groupByTaxCodePrefKey =
    isReceipts && organisationId
      ? `${RECEIPTS_GROUP_BY_TAX_CODE_KEY_PREFIX}-${organisationId}`
      : null;
  const mapItemsPrefKey =
    isReceipts && organisationId && supportsMapItems
      ? `${RECEIPTS_MAP_ITEMS_KEY_PREFIX}-${organisationId}`
      : null;
  const multiCurrencyModePrefKey =
    isReceipts && organisationId
      ? `${RECEIPTS_MULTI_CURRENCY_MODE_KEY_PREFIX}-${organisationId}`
      : null;
  const legacyMultiCurrencyModePrefKey =
    isReceipts && organisationId
      ? `${LEGACY_RECEIPTS_MULTI_CURRENCY_MODE_KEY_PREFIX}-${organisationId}`
      : null;
  const coaSkippedLoadedForKeyRef = React.useRef<string | null>(null);
  const groupByTaxCodeLoadedForKeyRef = React.useRef<string | null>(null);
  const mapItemsLoadedForKeyRef = React.useRef<string | null>(null);
  const multiCurrencyModeLoadedForKeyRef = React.useRef<string | null>(null);

  // Load whether COA was skipped from user preferences (once per organisation).
  React.useEffect(() => {
    if (!coaSkippedPrefKey || !currentUser?.userUuid) {
      coaSkippedLoadedForKeyRef.current = null;
      setCoaSelected(false);
      return;
    }
    if (coaSkippedLoadedForKeyRef.current === coaSkippedPrefKey) return;
    coaSkippedLoadedForKeyRef.current = coaSkippedPrefKey;
    const saved = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: coaSkippedPrefKey },
      currentUser.userUuid
    );
    setCoaSelected(saved === true);
  }, [coaSkippedPrefKey, currentUser?.userUuid, preferencesHook]);

  // Load groupByTaxCode from user preferences (once per organisation)
  React.useEffect(() => {
    if (!groupByTaxCodePrefKey || !currentUser?.userUuid) return;
    if (groupByTaxCodeLoadedForKeyRef.current === groupByTaxCodePrefKey) return;
    groupByTaxCodeLoadedForKeyRef.current = groupByTaxCodePrefKey;
    const saved = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: groupByTaxCodePrefKey },
      currentUser.userUuid
    );
    if (typeof saved === 'boolean') {
      setGroupByTaxCode(saved);
    }
  }, [groupByTaxCodePrefKey, currentUser?.userUuid, preferencesHook]);

  // Load mapItems from user preferences (once per organisation)
  React.useEffect(() => {
    if (!mapItemsPrefKey || !currentUser?.userUuid) {
      mapItemsLoadedForKeyRef.current = null;
      return;
    }
    const loadKey = `${mapItemsPrefKey}|${JSON.stringify(
      currentUser?.prefDetails?.[1] ?? ''
    )}`;
    if (mapItemsLoadedForKeyRef.current === loadKey) return;
    mapItemsLoadedForKeyRef.current = loadKey;
    const saved = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: mapItemsPrefKey },
      currentUser.userUuid
    );
    if (typeof saved === 'boolean') {
      setMapItems(saved);
    } else {
      setMapItems(false);
    }
  }, [
    mapItemsPrefKey,
    currentUser?.userUuid,
    currentUser?.prefDetails,
    preferencesHook,
  ]);

  React.useEffect(() => {
    if (!multiCurrencyModePrefKey || !currentUser?.userUuid) return;
    if (multiCurrencyModeLoadedForKeyRef.current === multiCurrencyModePrefKey)
      return;
    multiCurrencyModeLoadedForKeyRef.current = multiCurrencyModePrefKey;
    const saved =
      preferencesHook(
        PreferenceActions.GET,
        PreferenceSections.TRANSACTIONS,
        { name: multiCurrencyModePrefKey },
        currentUser.userUuid
      ) ??
      (legacyMultiCurrencyModePrefKey
        ? preferencesHook(
            PreferenceActions.GET,
            PreferenceSections.TRANSACTIONS,
            { name: legacyMultiCurrencyModePrefKey },
            currentUser.userUuid
          )
        : undefined);
    const parsedMode = parseMultiCurrencyModePreference(saved);
    const mode = parsedMode
      ? parsedMode === 'stop_upload' &&
        !entitySupportsMultiCurrencyStopUpload(organisation?.providerName)
        ? 'automatic_conversion'
        : parsedMode
      : getDefaultMultiCurrencyMode(organisation?.providerName);

    setMultiCurrencyMode(mode);
    const serializedMode = serializeMultiCurrencyModePreference(mode);
    if (saved !== serializedMode && multiCurrencyModePrefKey) {
      preferencesHook(
        PreferenceActions.SET,
        PreferenceSections.TRANSACTIONS,
        { name: multiCurrencyModePrefKey, value: serializedMode },
        currentUser.userUuid
      );
    }
  }, [
    legacyMultiCurrencyModePrefKey,
    multiCurrencyModePrefKey,
    currentUser?.userUuid,
    preferencesHook,
    organisation?.providerName,
  ]);

    const loadRules = React.useCallback(() => {
      dispatch(getRulesForApplicationConnectionRequest(organisationId));
    }, [dispatch, organisationId]);
  
    React.useEffect(() => {
      loadRules();
    }, [loadRules]);

  const persistMultiCurrencyMode = React.useCallback(
    (mode: ReceiptMultiCurrencyMode, options?: { notify?: boolean }) => {
      const didChange = mode !== multiCurrencyMode;
      setMultiCurrencyMode(mode);
      if (multiCurrencyModePrefKey && currentUser?.userUuid) {
        preferencesHook(
          PreferenceActions.SET,
          PreferenceSections.TRANSACTIONS,
          {
            name: multiCurrencyModePrefKey,
            value: serializeMultiCurrencyModePreference(mode),
          },
          currentUser.userUuid
        );

        if (options?.notify && didChange) {
          const optionLabel =
            mode === 'stop_upload'
              ? t('organisation.scanning-preferences.multi-currency-stop-title')
              : t(
                  'organisation.scanning-preferences.multi-currency-automatic-title'
                );
          dispatch(
            turnMessageOn({
              title: t(
                'organisation.scanning-preferences.multi-currency-saved-title'
              ),
              description: t(
                'organisation.scanning-preferences.multi-currency-saved-description',
                { option: optionLabel }
              ),
              type: MessageStates.SUCCESS,
            } as MessageData)
          );
        }
      }
    },
    [
      currentUser?.userUuid,
      dispatch,
      multiCurrencyMode,
      multiCurrencyModePrefKey,
      preferencesHook,
      t,
    ]
  );

  React.useEffect(() => {
    if (
      supportsStopUploadMultiCurrency ||
      multiCurrencyMode !== 'stop_upload'
    ) {
      return;
    }
    persistMultiCurrencyMode('automatic_conversion');
  }, [supportsStopUploadMultiCurrency, multiCurrencyMode]);

  const renderScanningPreferences = (
    disabled = false,
    variant: 'boxed' | 'drawer' = 'boxed'
  ) => {
    const selectedMultiCurrencyOptionLabel =
      multiCurrencyMode === 'stop_upload'
        ? t('organisation.scanning-preferences.multi-currency-stop-title')
        : t('organisation.scanning-preferences.multi-currency-automatic-title');
    const isDrawer = variant === 'drawer';

    return (
      <ScanningPreferencesBox $disabled={disabled} $variant={variant}>
        {!isDrawer ? (
          <ScanningPreferencesTitle>
            {t('organisation.scanning-preferences.title')}
          </ScanningPreferencesTitle>
        ) : null}

        <ScanningPreferenceRow>
          <ScanningPreferenceHeader>
            <ScanningPreferenceText>
              <ScanningPreferenceLabel>
                {t('organisation.group-lines-by-tax-code-label')}
              </ScanningPreferenceLabel>
              <ScanningPreferenceDescription>
                {t(
                  'organisation.scanning-preferences.group-by-tax-code-description'
                )}
              </ScanningPreferenceDescription>
            </ScanningPreferenceText>
            <Tooltip
              title={
                groupByTaxCode
                  ? t('common.toggle-on-click-to-turn-off')
                  : t('common.toggle-off-click-to-turn-on')
              }
            >
              <Switch
                size="small"
                checked={groupByTaxCode}
                disabled={disabled}
                className={groupByTaxCode ? 'switch-checked' : ''}
                aria-label={t('organisation.group-lines-by-tax-code-label')}
                onChange={(checked) => {
                  if (disabled) return;
                  setGroupByTaxCode(checked);
                  if (groupByTaxCodePrefKey && currentUser?.userUuid) {
                    preferencesHook(
                      PreferenceActions.SET,
                      PreferenceSections.TRANSACTIONS,
                      { name: groupByTaxCodePrefKey, value: checked },
                      currentUser.userUuid
                    );
                  }
                }}
              />
            </Tooltip>
          </ScanningPreferenceHeader>
        </ScanningPreferenceRow>

        {onOpenInterpretationRules && (
          <ScanningPreferenceRow>
            <MultiCurrencyToggle
              type="button"
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onOpenInterpretationRules();
              }}
            >
              <ScanningPreferenceText>
                <ScanningPreferenceLabel>
                  <ScanningPreferenceLabelWithIcon>
                    <ExtractionRulesPreferenceIcon aria-hidden />
                    {t('organisation.scanning-preferences.extraction-rules-label', {
                      count: extractionRulesCount,
                    })}
                  </ScanningPreferenceLabelWithIcon>
                </ScanningPreferenceLabel>
              </ScanningPreferenceText>
              <RightOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 12 }} />
            </MultiCurrencyToggle>
          </ScanningPreferenceRow>
        )}

        <ScanningPreferenceRow>
          <MultiCurrencyToggle
            type="button"
            aria-expanded={multiCurrencyOpen}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setMultiCurrencyOpen((open) => !open);
            }}
          >
            <ScanningPreferenceText>
              <ScanningPreferenceLabel>
                {t('organisation.scanning-preferences.multi-currency-label')}
              </ScanningPreferenceLabel>
              {!multiCurrencyOpen && (
                <ScanningPreferenceDescription>
                  {selectedMultiCurrencyOptionLabel}
                </ScanningPreferenceDescription>
              )}
            </ScanningPreferenceText>
            {multiCurrencyOpen ? (
              <DownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 12 }} />
            ) : (
              <RightOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: 12 }} />
            )}
          </MultiCurrencyToggle>

          {multiCurrencyOpen && (
            <MultiCurrencyPanel>
              <MultiCurrencyIntro>
                {t('organisation.scanning-preferences.multi-currency-intro')}
              </MultiCurrencyIntro>
              <MultiCurrencyRadioGroup
                value={multiCurrencyMode}
                disabled={disabled}
                onChange={(e) =>
                  persistMultiCurrencyMode(
                    e.target.value as ReceiptMultiCurrencyMode,
                    { notify: true }
                  )
                }
              >
                <Radio value="automatic_conversion">
                  <div>
                    <MultiCurrencyOptionTitle>
                      {t(
                        'organisation.scanning-preferences.multi-currency-automatic-title'
                      )}
                    </MultiCurrencyOptionTitle>
                    <MultiCurrencyOptionDescription>
                      {t(
                        'organisation.scanning-preferences.multi-currency-automatic-description'
                      )}
                    </MultiCurrencyOptionDescription>
                  </div>
                </Radio>
                <Tooltip
                  title={
                    supportsStopUploadMultiCurrency
                      ? undefined
                      : t(
                          'organisation.scanning-preferences.multi-currency-stop-disabled'
                        )
                  }
                >
                  <span>
                    <Radio
                      value="stop_upload"
                      disabled={!supportsStopUploadMultiCurrency}
                    >
                      <div>
                        <MultiCurrencyOptionTitle>
                          {t(
                            'organisation.scanning-preferences.multi-currency-stop-title'
                          )}
                        </MultiCurrencyOptionTitle>
                        <MultiCurrencyOptionDescription>
                          {t(
                            'organisation.scanning-preferences.multi-currency-stop-description'
                          )}
                        </MultiCurrencyOptionDescription>
                      </div>
                    </Radio>
                  </span>
                </Tooltip>
              </MultiCurrencyRadioGroup>
            </MultiCurrencyPanel>
          )}
        </ScanningPreferenceRow>

        {supportsMapItems && (
          <ScanningPreferenceRow>
            <ScanningPreferenceHeader>
              <ScanningPreferenceText>
                <ScanningPreferenceLabel>
                  {t('organisation.scanning-preferences.map-items-label')}
                </ScanningPreferenceLabel>
                <ScanningPreferenceDescription>
                  {t('organisation.scanning-preferences.map-items-description')}
                </ScanningPreferenceDescription>
              </ScanningPreferenceText>
              <Tooltip
                title={
                  mapItems
                    ? t('common.toggle-on-click-to-turn-off')
                    : t('common.toggle-off-click-to-turn-on')
                }
              >
                <Switch
                  size="small"
                  checked={mapItems}
                  disabled={disabled}
                  className={mapItems ? 'switch-checked' : ''}
                  aria-label={t(
                    'organisation.scanning-preferences.map-items-label'
                  )}
                  onChange={(checked) => {
                    if (disabled) return;
                    setMapItems(checked);
                    if (mapItemsPrefKey && currentUser?.userUuid) {
                      preferencesHook(
                        PreferenceActions.SET,
                        PreferenceSections.TRANSACTIONS,
                        { name: mapItemsPrefKey, value: checked },
                        currentUser.userUuid
                      );
                    }
                  }}
                />
              </Tooltip>
            </ScanningPreferenceHeader>
          </ScanningPreferenceRow>
        )}
      </ScanningPreferencesBox>
    );
  };

  const [receiptsTourOpen, setReceiptsTourOpen] = React.useState(false);
  const receiptsTourTriggeredRef = React.useRef(false);
  const receiptsTourStep1Ref = React.useRef<HTMLDivElement>(null);
  const receiptsTourStep2Ref = React.useRef<HTMLDivElement>(null);
  const receiptsTourStep3Ref = React.useRef<HTMLDivElement>(null);
  /** Prevent handling the same BATCH DONE multiple times (SSE may resend on reconnect). */
  const batchDoneProcessedRef = React.useRef<Set<string>>(new Set());
  const prevTrackedBatchIdRef = React.useRef<string | undefined>(undefined);
  const scanFilesRef = React.useRef(scanFiles);
  scanFilesRef.current = scanFiles;
  const currentStatusRef = React.useRef(currentStatus);
  currentStatusRef.current = currentStatus;
  const batchIdRef = React.useRef(batchId);
  batchIdRef.current = batchId;
  /**
   * After the user closes the completed "Uploaded files" card, Redux can still hold an
   * older in-progress snapshot while local state was advanced by SSE. Skipping the
   * Reduxâ†’local sync until the next upload avoids flashing "Extracting data" again.
   */
  const suppressReduxReceiptStatusSyncRef = React.useRef(false);
  /** True from choosing files until the server returns a new batchId. */
  const sessionUploadRef = React.useRef(false);
  const [sessionUploadActive, setSessionUploadActive] = React.useState(false);
  /** Redux status snapshot at the moment the user chose files. */
  const staleReceiptStatusKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!uploadInitFailed) return;
    sessionUploadRef.current = false;
    setSessionUploadActive(false);
    staleReceiptStatusKeyRef.current = null;
  }, [uploadInitFailed]);

  const organisationTaxRates = React.useMemo(
    () => resolveOrganisationTaxRatesForSetupCheck(resolvedOrganisation),
    [
      resolvedOrganisation?.taxRates,
      resolvedOrganisation?.taxRatesUnique,
      resolvedOrganisation?.hasTaxRates,
    ]
  );

  const hasTaxRates =
    organisationTaxRates.length > 0 ||
    (taxRatesForConnection?.length ?? 0) > 0 ||
    Boolean(resolvedOrganisation?.hasTaxRates);

  //console.log('', organisation?.chartOfAccounts, coaSelected);
  const hasChartOfAccounts =
    Array.isArray(resolvedOrganisation?.chartOfAccounts) &&
    resolvedOrganisation.chartOfAccounts.length > 0;
  const existingNotes: string | undefined =
    resolvedOrganisation?.organisationDetails?.notes;
  const profileStepCompleted = hasEntityProfileSection(existingNotes);
  const coaStepCompleted = hasChartOfAccounts || coaSelected;
  const hasScannedReceipt = hasTransactions;
  const taxRatesStepCompleted = hasTaxRates;
  /** Steps 1–3 required before scanning; panel must stay visible until all three pass. */
  const scanPrerequisitesMet = useReceiptsScanPrerequisitesMet(
    organisationId,
    resolvedOrganisation,
    { coaSkipped: coaSelected }
  );
  /** Step 4 starts as soon as a file is chosen (don’t wait for transactions). */
  const hasStartedFirstScan =
    hasScannedReceipt ||
    scanFiles.length > 0 ||
    Boolean(currentStatus?.files?.length);
  const allChecklistDone =
    isReceipts && scanPrerequisitesMet && hasStartedFirstScan;

  const checklistPrefKey =
    organisationId && isReceipts
      ? `${RECEIPTS_BEFORE_SCAN_CHECKLIST_DONE_KEY_PREFIX}-${organisationId}`
      : null;
  const checklistPrefCompleted =
    checklistPrefKey && currentUser?.userUuid
      ? preferencesHook(
          PreferenceActions.GET,
          PreferenceSections.TRANSACTIONS,
          { name: checklistPrefKey },
          currentUser.userUuid
        )
      : false;
  const receiptScanStepsCompleted =
    resolvedOrganisation?.receiptScanStepsCompleted === true;
  const prerequisitesEvaluationReady = Boolean(
    resolvedOrganisation &&
      resolvedOrganisation.organisationDetails != null &&
      // COA readiness: either we already know it's complete, or COA fetch has completed.
      (coaStepCompleted ||
        resolvedOrganisation.chartOfAccountsLoaded === true) &&
      // Tax rates readiness: either complete, explicitly none from backend, or load settled.
      (taxRatesStepCompleted ||
        resolvedOrganisation.hasTaxRates === false ||
        !gettingTaxes)
  );
  // Default visibility uses backend flag to avoid initial flicker on page load.
  // We still evaluate prerequisites and re-show if checks eventually fail.
  // Hide as soon as the first file is chosen so the scan animation below is visible.
  const shouldShowChecklist = isReceipts
    ? receiptScanStepsCompleted
      ? // Even with backend flag true, still show when:
        // 1) step 4 (first upload/scan) is not completed in preferences and
        //    no upload has started yet, or
        // 2) prerequisites checks finish and fail.
        (!checklistPrefCompleted && !hasStartedFirstScan) ||
        (prerequisitesEvaluationReady && !scanPrerequisitesMet)
      : !scanPrerequisitesMet || (!checklistPrefCompleted && !allChecklistDone)
    : false;

  /** Scan New Transactions box is enabled for receipts after profile, tax rates and COA. */
  const scanAreaEnabled = !isReceipts || scanPrerequisitesMet;
  const scanningPreferencesDisabled = isReceipts && !scanAreaEnabled;

  const canUploadMore = !isReceipts || !batchId || batchAllProcessed;

  const canUploadNow = !isReceipts ? true : scanAreaEnabled && canUploadMore;

  const openScanningPreferencesDrawer = React.useCallback(() => {
    if (scanningPreferencesDisabled) return;
    const prefsTitle = t('organisation.scanning-preferences.title');
    dispatch(
      turnDrawerOn({
        title: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <SettingOutlined
              aria-hidden
              style={{
                color: 'var(--color-primary, #41aaaf)',
                fontSize: 18,
              }}
            />
            {prefsTitle}
          </span>
        ),
        type: DrawerType.SCANNING_PREFERENCES,
        component: renderScanningPreferences(
          scanningPreferencesDisabled,
          'drawer'
        ),
      } satisfies DrawerData)
    );
  }, [
    dispatch,
    scanningPreferencesDisabled,
    t,
    groupByTaxCode,
    mapItems,
    multiCurrencyMode,
    multiCurrencyOpen,
    extractionRulesCount,
    onOpenInterpretationRules,
    supportsStopUploadMultiCurrency,
    supportsMapItems,
    groupByTaxCodePrefKey,
    mapItemsPrefKey,
    multiCurrencyModePrefKey,
    currentUser?.userUuid,
    preferencesHook,
  ]);

  // Keep drawer body in sync with preference toggles while it is open.
  React.useEffect(() => {
    if (!scanningPrefsDrawerOpen || scanningPreferencesDisabled) return;
    const prefsTitle = t('organisation.scanning-preferences.title');
    dispatch(
      turnDrawerOn({
        title: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <SettingOutlined
              aria-hidden
              style={{
                color: 'var(--color-primary, #41aaaf)',
                fontSize: 18,
              }}
            />
            {prefsTitle}
          </span>
        ),
        type: DrawerType.SCANNING_PREFERENCES,
        component: renderScanningPreferences(
          scanningPreferencesDisabled,
          'drawer'
        ),
      } satisfies DrawerData)
    );
    // Intentionally omit openScanningPreferencesDrawer to avoid identity churn loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on preference value changes only
  }, [
    scanningPrefsDrawerOpen,
    groupByTaxCode,
    mapItems,
    multiCurrencyMode,
    multiCurrencyOpen,
    extractionRulesCount,
    scanningPreferencesDisabled,
    supportsStopUploadMultiCurrency,
    supportsMapItems,
  ]);

  // After the first file is chosen, checklist collapses — bring the scan animation into view.
  const prevHasStartedFirstScanRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    prevHasStartedFirstScanRef.current = null;
  }, [organisationId]);
  React.useEffect(() => {
    if (prevHasStartedFirstScanRef.current === null) {
      // Hydrate without scrolling (e.g. returning mid-upload).
      prevHasStartedFirstScanRef.current = hasStartedFirstScan;
      return;
    }
    const wasStarted = prevHasStartedFirstScanRef.current;
    prevHasStartedFirstScanRef.current = hasStartedFirstScan;
    if (!isReceipts || wasStarted || !hasStartedFirstScan) return;
    // PWA: checklist collapse already reveals the card; scrollIntoView can
    // over-scroll the tab panel and leave a white band over the bottom of the card.
    if (pwaMobileNav) return;
    const frame = window.requestAnimationFrame(() => {
      scanProgressCardRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hasStartedFirstScan, isReceipts, organisationId, pwaMobileNav]);

  const prevScanPrerequisitesMetRef = React.useRef(false);
  React.useEffect(() => {
    const wasMet = prevScanPrerequisitesMetRef.current;
    const isNowMet = scanPrerequisitesMet;
    prevScanPrerequisitesMetRef.current = isNowMet;

    if (!isReceipts || !organisationId) return;
    if (receiptScanStepsCompleted) return;
    // Fire once when step 1-3 become complete (in any order).
    if (!wasMet && isNowMet) {
      dispatch(completeScanStepsRequest(organisationId));
    }
  }, [
    dispatch,
    isReceipts,
    organisationId,
    scanPrerequisitesMet,
    receiptScanStepsCompleted,
  ]);

  //const countries = useSelector(getCommonCountries) as Country[];

  const openTaxRatesDrawer = React.useCallback(() => {
    dispatch(getTaxRateTemplatesRequest());
    dispatch(fetchReturnTypesRequest(organisation?.connectionId));
    const entityCountryId = organisation?.returnTypesEdit?.[0]?.countryId;
    const drawerData: DrawerData = {
      title: t('organisation.add-new-taxes'),
      type: DrawerType.ADD_NEW_TAXES,
      component: (
        <AddNewTaxesForm
          loading={gettingTaxes || returnLoading}
          connectionId={organisation?.connectionId}
          initialCountryId={entityCountryId}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [
    dispatch,
    organisation?.connectionId,
    organisation?.returnTypesEdit,
    organisation?.taxRates,
    gettingTaxes,
    returnLoading,
  ]);

  const openChartOfAccountsDrawer = React.useCallback(() => {
    // Prefer a fixed Malta country (iso === 'MT') for COA templates, matching ChartOfAccountsPromotionBanner.
    const malta = Array.isArray(countries)
      ? countries.find((c) => c.iso === 'MT')
      : undefined;
    const maltaCountryId = malta?.id;

    // Fallback: if Malta is missing, retain original behaviour and use the entity's first returnTypesEdit countryId.
    const entityCountryId =
      maltaCountryId ?? organisation?.returnTypesEdit?.[0]?.countryId;

    const drawerData: DrawerData = {
      title: t('organisation.add-account'),
      type: DrawerType.ADD_CHART_OF_ACCOUNTS,
      width: 560,
      component: (
        <AddChartOfAccountsForm
          loading={gettingChartOfAccounts}
          organisationId={organisation?.uuid}
          initialCountryId={entityCountryId}
          onSuccess={() => dispatch(turnDrawerOff())}
          onSkipCoa={() => {
            setCoaSelected(true);
            if (coaSkippedPrefKey && currentUser?.userUuid) {
              preferencesHook(
                PreferenceActions.SET,
                PreferenceSections.TRANSACTIONS,
                { name: coaSkippedPrefKey, value: true },
                currentUser.userUuid
              );
            }
            dispatch(turnDrawerOff());
          }}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  }, [
    dispatch,
    organisation?.uuid,
    organisation?.returnTypesEdit,
    gettingChartOfAccounts,
    countries,
    coaSkippedPrefKey,
    currentUser?.userUuid,
    preferencesHook,
  ]);

  const handleReceiptsTourClose = React.useCallback(() => {
    setReceiptsTourOpen(false);
    if (currentUser?.userUuid) {
      preferencesHook(
        PreferenceActions.SET,
        PreferenceSections.TRANSACTIONS,
        { name: RECEIPTS_ONBOARDING_TOUR_SHOWN_KEY, value: true },
        currentUser.userUuid
      );
    }
  }, [currentUser?.userUuid, preferencesHook]);

  const receiptsTourSteps: ScopeTourStep[] = React.useMemo(
    () => [
      {
        target: () => receiptsTourStep1Ref.current ?? document.body,
        description: (
          <div className="scope-tour-step-description">
            {t('organisation.receipts-tour-step1-desc')}
          </div>
        ),
        cover: (
          <div className="scope-tour-step-cover">
            <div className={SCOPE_TOUR_STEP_TITLE_CLASS}>
              {t('organisation.receipts-tour-step1-title')}
            </div>
          </div>
        ),
        placement: 'right',
      },
      {
        target: () => receiptsTourStep2Ref.current ?? document.body,
        description: (
          <div className="scope-tour-step-description">
            {t('organisation.receipts-tour-step2-desc')}
          </div>
        ),
        cover: (
          <div className="scope-tour-step-cover">
            <div className={SCOPE_TOUR_STEP_TITLE_CLASS}>
              {t('organisation.receipts-tour-step2-title')}
            </div>
          </div>
        ),
        placement: 'left',
      },
      ...(!pwaMobileNav && variant === 'receipts'
        ? [
            {
              target: () => receiptsTourStep3Ref.current ?? document.body,
              description: (
                <div className="scope-tour-step-description">
                  {t('organisation.receipts-tour-step3-desc')}
                </div>
              ),
              cover: (
                <div className="scope-tour-step-cover">
                  <div className={SCOPE_TOUR_STEP_TITLE_CLASS}>
                    {t('organisation.receipts-tour-step3-title')}
                  </div>
                </div>
              ),
              placement: 'top' as const,
            },
          ]
        : []),
    ],
    [pwaMobileNav, t, variant]
  );

  // Show receipts onboarding tour on first access when checklist is visible
  React.useEffect(() => {
    if (pwaMobileNav) return;
    if (
      !isReceipts ||
      receiptsTourTriggeredRef.current ||
      !currentUser?.userUuid ||
      !shouldShowChecklist
    )
      return;
    if (allChecklistDone || (checklistPrefCompleted && scanPrerequisitesMet))
      return;
    const tourShown = preferencesHook(
      PreferenceActions.GET,
      PreferenceSections.TRANSACTIONS,
      { name: RECEIPTS_ONBOARDING_TOUR_SHOWN_KEY },
      currentUser.userUuid
    );
    if (tourShown === true) return;
    receiptsTourTriggeredRef.current = true;
    const timer = setTimeout(() => setReceiptsTourOpen(true), 400);
    return () => clearTimeout(timer);
  }, [
    isReceipts,
    allChecklistDone,
    checklistPrefCompleted,
    scanPrerequisitesMet,
    shouldShowChecklist,
    currentUser?.userUuid,
    preferencesHook,
    pwaMobileNav,
  ]);

  // Mark step 2 (COA) as done when user has skipped via "Skip for now" (coaSelected).
  // Account saved state (hasChartOfAccounts) is derived from organisation.chartOfAccounts.

  // Persist "Before you scan" checklist completion only when all four steps are done.
  // Never persist while scan prerequisites (steps 1–3) are incomplete.
  // Skip SET if already saved to prevent loop: SET -> success -> currentUser updates -> preferencesHook
  // changes -> effect re-runs -> SET again.
  React.useEffect(() => {
    if (
      !allChecklistDone ||
      !scanPrerequisitesMet ||
      !checklistPrefKey ||
      !currentUser?.userUuid
    )
      return;
    if (checklistPrefCompleted) return; // Already persisted, avoid redundant SET
    preferencesHook(
      PreferenceActions.SET,
      PreferenceSections.TRANSACTIONS,
      { name: checklistPrefKey, value: true },
      currentUser.userUuid
    );
  }, [
    allChecklistDone,
    scanPrerequisitesMet,
    checklistPrefKey,
    checklistPrefCompleted,
    currentUser?.userUuid,
    preferencesHook,
  ]);

  // When we (re)mount (e.g. user came back to Overview tab), reset to drop panel once.
  const initialStatusSyncKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    suppressReduxReceiptStatusSyncRef.current = false;
  }, [organisationId]);

  React.useEffect(() => {
    setScanFiles([]);
    setCurrentStatus(null);

    const status = unwrapReceiptUploadStatus(lastReceiptUploadStatus);
    initialStatusSyncKeyRef.current =
      receiptUploadStatusSyncKey(status) || null;
    // Only depend on isReceipts so we don't clear local scan state
    // every time the server status updates (which would hide progress).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReceipts]);

  React.useEffect(() => {
    if (!isReceipts || !optimisticRescanFileName) return;

    // Show the processing card immediately after switching from Transactions
    // to Capture, instead of waiting for the rescan API + status fetch.
    suppressReduxReceiptStatusSyncRef.current = false;
    setCurrentStatus(null);
    setBatchAllProcessed(false);
    setScanFiles([
      {
        id: `optimistic-rescan-${
          organisationId ?? 'unknown'
        }-${optimisticRescanFileName}`,
        name: optimisticRescanFileName,
        stage: 'processing_ai',
        removable: false,
      },
    ]);
    onReceiptsBatchStatusChange?.(true);
    onOptimisticRescanShown?.();
  }, [
    isReceipts,
    optimisticRescanFileName,
    organisationId,
    onReceiptsBatchStatusChange,
    onOptimisticRescanShown,
  ]);

  const lastReceiptUploadStatusRef = React.useRef(lastReceiptUploadStatus);
  lastReceiptUploadStatusRef.current = lastReceiptUploadStatus;

  // When batchId appears, fetch initial status. Only reset "all processed" when
  // the id actually changes — remounting after the phone camera must not clear
  // a batch that already finished.
  React.useEffect(() => {
    if (!isReceipts || !batchId || !organisationId) {
      if (!batchId) prevTrackedBatchIdRef.current = undefined;
      return;
    }
    const isNewBatchId = prevTrackedBatchIdRef.current !== batchId;
    if (!isNewBatchId) return;

    const wasSessionUpload = sessionUploadRef.current;
    prevTrackedBatchIdRef.current = batchId;
    sessionUploadRef.current = false;
    setSessionUploadActive(false);
    suppressReduxReceiptStatusSyncRef.current = false;

    const existing = unwrapReceiptUploadStatus(
      lastReceiptUploadStatusRef.current
    );
    const existingFiles = existing?.files ?? [];
    const existingIsTerminal =
      existingFiles.length > 0 &&
      existingFiles.every((file) => !isUploadStatusProcessing(file.status));

    // Remount with the previous completed batch: keep it marked done so we
    // do not reconnect SSE or treat it as a new in-flight upload.
    if (existingIsTerminal && !wasSessionUpload) {
      batchDoneProcessedRef.current.add(String(batchId));
      setBatchAllProcessed(true);
      return;
    }

    setBatchAllProcessed(false);
    batchDoneProcessedRef.current.clear();
    dispatch(fetchLastReceiptUploadStatusRequest(organisationId));
  }, [isReceipts, batchId, organisationId, dispatch]);

  // Keep currentStatus in sync with Redux (stable key so we don't re-run when ref changes).
  // Skip first run after mount so we stay on drop panel when coming back to tab.
  const statusFromRedux = isReceipts
    ? unwrapReceiptUploadStatus(lastReceiptUploadStatus)
    : null;
  const statusSyncKey = receiptUploadStatusSyncKey(statusFromRedux);
  const statusFromReduxRef = React.useRef(statusFromRedux);
  statusFromReduxRef.current = statusFromRedux;

  React.useEffect(() => {
    if (!isReceipts || !statusSyncKey) return;
    if (statusSyncKey === initialStatusSyncKeyRef.current) return;

    const isStaleSessionSnapshot =
      Boolean(staleReceiptStatusKeyRef.current) &&
      statusSyncKey === staleReceiptStatusKeyRef.current;

    if (sessionUploadRef.current && isStaleSessionSnapshot) {
      return;
    }

    if (sessionUploadRef.current) {
      sessionUploadRef.current = false;
      setSessionUploadActive(false);
      suppressReduxReceiptStatusSyncRef.current = false;
      staleReceiptStatusKeyRef.current = null;
    } else if (suppressReduxReceiptStatusSyncRef.current) {
      return;
    }

    const next = statusFromReduxRef.current;
    if (!next) return;

    const files = next.files ?? [];
    const processing = files.some((file) =>
      isUploadStatusProcessing(file.status)
    );
    const alreadyDone = Boolean(
      batchId && batchDoneProcessedRef.current.has(String(batchId))
    );
    const localAlreadyTerminal =
      (currentStatusRef.current?.files?.length ?? 0) > 0 &&
      currentStatusRef.current!.files.every(
        (file) => !isUploadStatusProcessing(file.status)
      );

    if (alreadyDone || localAlreadyTerminal) {
      const hasLocalUploadState =
        scanFiles.length > 0 || (currentStatus?.files?.length ?? 0) > 0;
      if (!processing && files.length > 0 && hasLocalUploadState) {
        setCurrentStatus(next);
      }
      setBatchAllProcessed(true);
      return;
    }

    const hasTerminalOnlyFiles =
      files.length > 0 &&
      files.every((file) => !isUploadStatusProcessing(file.status));
    const hasLocalUploadState =
      scanFiles.length > 0 || (currentStatus?.files?.length ?? 0) > 0;

    if (hasTerminalOnlyFiles && !hasLocalUploadState) {
      // User re-entered Capture tab and Redux still has a historical completed batch.
      // Keep the panel in its initial drop state instead of restoring old success UI.
      setCurrentStatus(null);
      setScanFiles([]);
      setBatchAllProcessed(true);
      onReceiptsBatchStatusChange?.(false);
      return;
    }

    if (hasTerminalOnlyFiles && hasLocalUploadState) {
      if (isStaleSessionSnapshot) {
        return;
      }
      const belongsToCurrentBatch = files.some((file) =>
        sameReceiptId(file.importId, batchId)
      );
      const matchesLocalNames = files.some((file) =>
        scanFilesRef.current.some(
          (scan) =>
            normalizeUploadedFileName(scan.name) ===
            normalizeUploadedFileName(String(file.fileName ?? ''))
        )
      );
      if (!belongsToCurrentBatch && !matchesLocalNames) {
        return;
      }
      setCurrentStatus(next);
      setScanFiles((prev) =>
        prev.map((scan) => {
          const match = files.find(
            (file) =>
              normalizeUploadedFileName(String(file.fileName ?? '')) ===
              normalizeUploadedFileName(scan.name)
          );
          if (!match) {
            return scan.stage === 'error' ? scan : { ...scan, stage: 'done' };
          }
          return { ...scan, stage: scanStageFromReceiptStatus(match.status) };
        })
      );
      setBatchAllProcessed(true);
      if (batchId) {
        batchDoneProcessedRef.current.add(String(batchId));
      }
      onReceiptsBatchStatusChange?.(false);
      return;
    }

    const hasOnlyDeletedFiles =
      files.length > 0 &&
      files.every((file) => isDeletedReceiptStatus(file.status));

    if (hasOnlyDeletedFiles) {
      setCurrentStatus(null);
      setScanFiles([]);
      setBatchAllProcessed(true);
      onReceiptsBatchStatusChange?.(false);
      return;
    }

    setCurrentStatus(next);
    if (!processing && files.length > 0) {
      setBatchAllProcessed(true);
    }
  }, [
    isReceipts,
    statusSyncKey,
    scanFiles.length,
    currentStatus?.files?.length,
    batchId,
    onReceiptsBatchStatusChange,
  ]);

  const finalizeReceiptBatch = React.useCallback(
    (importId: string) => {
      const normalizedId = String(importId ?? '').trim();
      if (!normalizedId || batchDoneProcessedRef.current.has(normalizedId)) {
        return;
      }
      batchDoneProcessedRef.current.add(normalizedId);
      setScanFiles((prev) =>
        prev.map((file) =>
          file.stage === 'error' ? file : { ...file, stage: 'done' }
        )
      );
      setCurrentStatus((prev) => {
        if (prev?.files?.length) {
          return {
            ...prev,
            status: 'DONE',
            files: markReceiptFilesTerminal(prev.files),
          };
        }
        const fromScan = scanFilesRef.current;
        if (!fromScan.length) {
          return prev ? { ...prev, status: 'DONE' } : { status: 'DONE', files: [] };
        }
        return {
          status: 'DONE',
          files: receiptFilesFromScan(fromScan),
        };
      });
      setBatchAllProcessed(true);
      onReceiptsBatchStatusChange?.(false);
      onReceiptsBatchComplete?.(normalizedId);
      if (organisationId) {
        dispatch(fetchLastReceiptUploadStatusRequest(organisationId));
        dispatch(fetchExcelFilesMetadataRequest(organisationId));
        dispatch(fetchAllExcelTransactionsRequest(organisationId, 1, 10));

        const connectionId = resolvedOrganisation?.connectionId;
        if (connectionId) {
          dispatch(refreshConnectionTaxRatesRequest(connectionId, true));
          dispatch(
            fetchChartOfAccountsForOrganisationRequest(organisationId)
          );
          dispatch(fetchOrganisationContactsRequest(connectionId));
        }
      }
    },
    [
      dispatch,
      onReceiptsBatchComplete,
      onReceiptsBatchStatusChange,
      organisationId,
      resolvedOrganisation?.connectionId,
    ]
  );

  const applySseMessage = React.useCallback(
    (raw: string) => {
      if (!isReceipts || !raw) return;
      try {
        const parsed = JSON.parse(raw) as {
          id?: string;
          status?: UploadStatus;
          message?: string;
          type?: string;
        };
        const updatedImportId = parsed.id;
        const newStatus = parsed.status;
        const errorReason = parsed.message || '';
        const messageType = (parsed.type ?? 'FILE').toUpperCase();
        const currentBatchId = String(batchIdRef.current ?? '').trim();
        if (sessionUploadRef.current) {
          return;
        }

        if (
          messageType === 'BATCH' &&
          sameReceiptId(updatedImportId, currentBatchId)
        ) {
          const batchStatus = String(newStatus ?? '').toUpperCase();
          const batchTerminal =
            batchStatus === 'DONE' ||
            Boolean(newStatus && !isUploadStatusProcessing(newStatus));
          if (batchTerminal) {
            if (batchStatus !== 'DONE' && newStatus) {
              setCurrentStatus((prev) => {
                const source =
                  prev?.files?.length
                    ? prev.files
                    : receiptFilesFromScan(scanFilesRef.current);
                return {
                  status: String(newStatus),
                  files: source.map((file) => ({
                    ...file,
                    status: isUploadStatusProcessing(file.status)
                      ? newStatus
                      : file.status || newStatus,
                    errorReason: errorReason || file.errorReason,
                  })),
                };
              });
              setScanFiles((prev) =>
                prev.map((file) =>
                  file.stage === 'error' || file.stage === 'done'
                    ? file
                    : {
                        ...file,
                        stage: isUploadStatusError(newStatus)
                          ? 'error'
                          : 'done',
                      }
                )
              );
            }
            finalizeReceiptBatch(String(updatedImportId || currentBatchId));
            return;
          }
        }

        if (messageType !== 'FILE' || !newStatus) return;

        const existing = currentStatusRef.current?.files ?? [];
        let nextFiles: ReceiptUploadStatusFile[];

        if (!existing.length) {
          const optimisticName = scanFilesRef.current[0]?.name ?? '';
          nextFiles = [
            {
              importId: String(updatedImportId ?? ''),
              fileName: optimisticName,
              uploadDate: '',
              totalRows: 0,
              fileType: '',
              accountDetailsId: '',
              status: newStatus,
              user: null,
              errorReason,
            },
          ];
        } else if (
          existing.some((file) => sameReceiptId(file.importId, updatedImportId))
        ) {
          nextFiles = existing.map((file) =>
            sameReceiptId(file.importId, updatedImportId)
              ? { ...file, status: newStatus, errorReason }
              : file
          );
        } else if (existing.length === 1) {
          nextFiles = [
            {
              ...existing[0],
              importId: String(updatedImportId ?? existing[0].importId),
              status: newStatus,
              errorReason,
            },
          ];
        } else {
          nextFiles = existing;
        }

        setCurrentStatus((prev) => ({
          ...(prev ?? { status: '', files: nextFiles }),
          files: nextFiles,
        }));
        setScanFiles((prev) =>
          prev.map((scan, index) => {
            const match =
              nextFiles.find((file) =>
                sameReceiptId(file.importId, updatedImportId)
              ) ?? nextFiles[index];
            if (!match) return scan;
            return {
              ...scan,
              stage: scanStageFromReceiptStatus(match.status),
            };
          })
        );

        if (
          nextFiles.length > 0 &&
          nextFiles.every((file) => !isUploadStatusProcessing(file.status)) &&
          currentBatchId
        ) {
          finalizeReceiptBatch(currentBatchId);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse SSE message:', e);
      }
    },
    [isReceipts, finalizeReceiptBatch]
  );

  // When fetched status shows batch is complete, enable upload (handles "return to page" scenario)
  React.useEffect(() => {
    if (!isReceipts || !batchId) return;
    if (sessionUploadRef.current) return;
    const status = unwrapReceiptUploadStatus(lastReceiptUploadStatus);
    if (!status) return;

    const files = status.files ?? [];

    // Empty files + batchId exists: treat as complete only when this tab is not
    // showing an in-flight upload (e.g. old batch no longer in API).
    if (files.length === 0) {
      const hasLocalUploadState =
        scanFilesRef.current.length > 0 ||
        (currentStatusRef.current?.files?.length ?? 0) > 0;
      if (!hasLocalUploadState) {
        setBatchAllProcessed(true);
      }
      return;
    }

    const allFilesTerminal = files.every(
      (f: ReceiptUploadStatusFile) => !isUploadStatusProcessing(f.status)
    );
    if (allFilesTerminal || String(status.status).toUpperCase() === 'DONE') {
      const hasLocalUploadState =
        scanFilesRef.current.length > 0 ||
        (currentStatusRef.current?.files?.length ?? 0) > 0;
      const belongsToCurrentBatch = files.some((file) =>
        sameReceiptId(file.importId, batchId)
      );
      if (hasLocalUploadState) {
        const isStaleSnapshot =
          Boolean(staleReceiptStatusKeyRef.current) &&
          receiptUploadStatusSyncKey(status) ===
            staleReceiptStatusKeyRef.current;
        if (!belongsToCurrentBatch && isStaleSnapshot) return;
        finalizeReceiptBatch(String(batchId));
      } else {
        setBatchAllProcessed(true);
      }
    }
  }, [isReceipts, batchId, lastReceiptUploadStatus, finalizeReceiptBatch]);

  const addScanFiles = React.useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const { allowed, rejected } = partitionFilesByValidation(
        fileArray,
        validateReceiptUploadFile
      );

      const limited = allowed.slice(0, MAX_SCAN_FILES);

      if (!limited.length) {
        if (rejected.length) {
          dispatch(
            turnMessageOn({
              title: t('common.unsupported-file-type-title'),
              description: t(
                'common.only-bmp-jpeg-jpg-gif-png-and-pdf-files-can-be-uploaded'
              ),
              type: MessageStates.WARNING,
            })
          );
        }
        return;
      }

      if (rejected.length) {
        dispatch(
          turnMessageOn({
            title: t('organisation.some-files-skipped-title'),
            description: t('organisation.some-files-skipped-description'),
            type: MessageStates.WARNING,
          })
        );
      }

      const newBatchFiles: ScanFileItem[] = limited.map((file, index) => ({
        id: `${file.name}-${Date.now()}-${index}`,
        file,
        name: file.name,
        stage: 'uploading_file',
        removable: true,
      }));

      if (isReceipts && organisationId) {
        staleReceiptStatusKeyRef.current = receiptUploadStatusSyncKey(
          unwrapReceiptUploadStatus(lastReceiptUploadStatusRef.current)
        );
        sessionUploadRef.current = true;
        setSessionUploadActive(true);
        suppressReduxReceiptStatusSyncRef.current = true;
        batchDoneProcessedRef.current.clear();
        initialStatusSyncKeyRef.current = null;
        setCurrentStatus(null);
        setBatchAllProcessed(false);
        setScanFiles(() => newBatchFiles);
        if (onReceiptsBatchStatusChange) {
          onReceiptsBatchStatusChange(true);
        }
        // Pass groupByTaxCode so API adds ?grouped=true when switch is on
        dispatch(uploadReceiptRequest(organisationId, limited, groupByTaxCode));
      } else if (organisationId) {
        setScanFiles((prev) => [...prev, ...newBatchFiles]);
        dispatch(uploadReceiptRequest(organisationId, limited));
      } else {
        setScanFiles((prev) => [...prev, ...newBatchFiles]);
      }

      if (onFilesUploaded) onFilesUploaded();
    },
    [
      dispatch,
      organisationId,
      onFilesUploaded,
      isReceipts,
      groupByTaxCode,
      onReceiptsBatchStatusChange,
    ]
  );

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!isReceipts || !canUploadNow) return;
      const files = e.dataTransfer.files;
      if (files?.length) addScanFiles(files);
    },
    [isReceipts, canUploadNow, addScanFiles]
  );

  const onDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (isReceipts && canUploadNow) setIsDragOver(true);
    },
    [isReceipts, canUploadNow]
  );

  const onDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const onFileInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!canUploadNow) return;
      const files = e.target.files;
      if (files?.length) addScanFiles(files);
      e.target.value = '';
    },
    [canUploadNow, addScanFiles]
  );

  const removeScanFile = React.useCallback((id: string) => {
    setScanFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const findLocalScanFile = React.useCallback(
    (fileName: string): File | undefined => {
      const key = normalizeUploadedFileName(fileName);
      const match = scanFiles.find(
        (item) => normalizeUploadedFileName(item.name) === key && item.file
      );
      return match?.file;
    },
    [scanFiles]
  );

  const getCachedAttachmentsForImportId = React.useCallback(
    (importId: string | undefined): TransactionAttachment[] => {
      const normalizedImportId = String(importId ?? '').trim();
      if (!normalizedImportId) return [];
      if (!allExcelTransactionsPage) {
        return [];
      }
      if (allExcelTransactionsPage.organisationId !== organisationId) {
        return [];
      }
      const transactions = (allExcelTransactionsPage.transactions ??
        []) as Array<ValidatedExcelTransaction & { importId?: string | null }>;
      const row = transactions.find(
        (tx) => String(tx.importId ?? '').trim() === normalizedImportId
      );
      const uuid = String(row?.uuid ?? '').trim();
      if (
        !uuid ||
        lastFetchedAttachments?.transactionId !== uuid ||
        !lastFetchedAttachments.attachments?.length
      ) {
        return [];
      }
      return lastFetchedAttachments.attachments;
    },
    [
      allExcelTransactionsPage?.organisationId,
      allExcelTransactionsPage?.transactions,
      organisationId,
      lastFetchedAttachments,
    ]
  );

  const canPreviewUploadedFile = React.useCallback(
    (fileName: string, importId?: string) =>
      !!findLocalScanFile(fileName) ||
      getCachedAttachmentsForImportId(importId).length > 0,
    [findLocalScanFile, getCachedAttachmentsForImportId]
  );

  const openUploadedFilePreview = React.useCallback(
    async (fileName: string, importId?: string) => {
      const cached = getCachedAttachmentsForImportId(importId);
      if (cached.length > 0) {
        setUploadPanelAttachmentPreview({ fileName, attachments: cached });
        return;
      }

      const localFile = findLocalScanFile(fileName);
      if (!localFile) return;

      const cacheKey = normalizeUploadedFileName(fileName);
      setUploadPanelAttachmentLoading(true);
      try {
        let attachment = localAttachmentCacheRef.current.get(cacheKey);
        if (!attachment) {
          attachment = await buildTransactionAttachmentFromFile(localFile);
          localAttachmentCacheRef.current.set(cacheKey, attachment);
        }
        setUploadPanelAttachmentPreview({
          fileName,
          attachments: [attachment],
        });
      } finally {
        setUploadPanelAttachmentLoading(false);
      }
    },
    [findLocalScanFile, getCachedAttachmentsForImportId]
  );

  const renderUploadedFileNameCell = React.useCallback(
    (fileName: string, importId?: string) => {
      const canPreview = canPreviewUploadedFile(fileName, importId);
      return (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: '1 1 0',
            minWidth: 0,
          }}
        >
          <span
            role="button"
            tabIndex={canPreview ? 0 : -1}
            aria-disabled={!canPreview}
            aria-label={t('organisation.uploaded-file-open-attachment-aria', {
              fileName,
            })}
            onClick={(e) => {
              e.stopPropagation();
              if (canPreview) {
                void openUploadedFilePreview(fileName, importId);
              }
            }}
            onKeyDown={(e) => {
              if (!canPreview) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                void openUploadedFilePreview(fileName, importId);
              }
            }}
            style={{
              cursor: canPreview ? 'pointer' : 'default',
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
              opacity: canPreview ? 1 : 0.35,
            }}
          >
            <IconAttachment
              className="transaction-action-icon"
              style={{ width: 22, height: 22 }}
            />
          </span>
          <span
            style={{
              flex: '1 1 0',
              minWidth: 0,
              fontSize: '14px',
              color: 'var(--color-text-dark, #042a2a)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={fileName}
          >
            {fileName}
          </span>
        </span>
      );
    },
    [canPreviewUploadedFile, openUploadedFilePreview, t]
  );

  const clearScanFiles = React.useCallback(() => {
    setScanFiles([]);
  }, []);

  const dismissCompletedReceiptsUploadPanel = React.useCallback(() => {
    sessionUploadRef.current = false;
    setSessionUploadActive(false);
    staleReceiptStatusKeyRef.current = null;
    suppressReduxReceiptStatusSyncRef.current = true;
    setCurrentStatus(null);
    setScanFiles([]);
    setBatchAllProcessed(true);
    // Clear the cached batch in Redux too. Otherwise the next upload briefly
    // re-syncs this stale completed status into local state and flashes the
    // old "Uploaded files" card before the new batch status arrives.
    batchDoneProcessedRef.current.clear();
    dispatch(clearReceiptUploadStatus());
    onReceiptsBatchStatusChange?.(false);
  }, [dispatch, onReceiptsBatchStatusChange]);

  const triggerScanFileInput = React.useCallback(() => {
    document.getElementById('scan-files-input')?.click();
  }, []);

  const openWebcamOrFilePicker = React.useCallback(() => {
    if (desktopWebcamAvailable) {
      setWebcamOpen(true);
      return;
    }
    triggerScanFileInput();
  }, [desktopWebcamAvailable, triggerScanFileInput]);

  React.useEffect(() => {
    if (!scanCameraPendingRef.current || !isReceipts || !canUploadNow) {
      return;
    }

    scanCameraPendingRef.current = false;
    navigate(`${location.pathname}${stripPwaScanCameraParam(location.search)}`, {
      replace: true,
    });

    const tryOpenPicker = (attempt = 0) => {
      if (desktopWebcamAvailable) {
        setWebcamOpen(true);
        return;
      }
      const input = document.getElementById('scan-files-input');
      if (input) {
        input.click();
        return;
      }
      if (attempt < 8) {
        window.setTimeout(() => tryOpenPicker(attempt + 1), 100);
      }
    };

    const timerId = window.setTimeout(() => tryOpenPicker(), 50);

    return () => window.clearTimeout(timerId);
  }, [
    isReceipts,
    canUploadNow,
    location.pathname,
    location.search,
    navigate,
    desktopWebcamAvailable,
  ]);

  const renderChecklistCard = () => {
    if (!shouldShowChecklist) return null;

    const openEntityProfileDrawer = () => {
      if (!organisationId) return;

      const drawerData: DrawerData = {
        title: t('organisation.entity-profile-title'),
        subtitle: t('organisation.entity-profile-subtitle'),
        type: DrawerType.EDIT,
        width: 520,
        drawerContentPadding: '0 0 0 12px',
        component: (
          <EntityProfileDrawerContent
            organisationId={organisationId}
            existingNotes={existingNotes}
          />
        ),
      };

      dispatch(turnDrawerOn(drawerData));
    };

    return (
      <StyledCard
        $nopadding
        fullsize
        style={{
          position: 'relative',
          boxShadow: pwaMobileNav ? 'none' : '0px 4px 10px 0px #0000000D',
          marginBottom: '20px',
          display: 'flex',
          fontSize: 14,
          fontWeight: 400,
          padding: '24px 0',
          borderRadius: '8px',
          backgroundColor: pwaMobileNav
            ? 'transparent'
            : 'var(--color-surface-raised, #ffffff)',
        }}
        width="100%"
      >
        {firstBoxTopRightExtras ? (
          <div
            style={{
              position: 'absolute',
              top: 20,
              right: 24,
              zIndex: 2,
            }}
          >
            {firstBoxTopRightExtras}
          </div>
        ) : null}
        <BeforeYouScanLayout>
          <BeforeYouScanSteps ref={receiptsTourStep1Ref}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: '20px',
              }}
            >
              <CardTitleLargeSpan style={{ display: 'block', marginBottom: 0 }}>
                {t('organisation.before-you-scan-title')}
              </CardTitleLargeSpan>
              {!pwaMobileNav ? (
                <QuestionMarkTooltip
                  title={t('organisation.receipts-tour-show-tip')}
                  onClick={() => setReceiptsTourOpen(true)}
                />
              ) : null}
            </div>
            <CardDescSpan
              style={{
                marginBottom: '20px',
                color: 'var(--color-text-muted, #5a6a6a)',
                fontSize: '14px',
                display: 'block',
              }}
            >
              {t('organisation.before-you-scan-subtitle')}
            </CardDescSpan>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                paddingLeft: 30,
                margin: 0,
              }}
            >
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 0',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary, #41aaaf)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  1
                </div>
                <BeforeYouScanStepContent>
                  <BeforeYouScanStepLabel>
                    {t('organisation.entity-profile-title')}
                  </BeforeYouScanStepLabel>
                  {profileStepCompleted ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'var(--color-primary, #41aaaf)',
                      }}
                    >
                      <CheckCircleOutlined style={{ fontSize: 16 }} />
                      {t('common.done')}
                    </span>
                  ) : (
                    <ScopeButton
                      type="dark-orange"
                      width="100px"
                      height="32px"
                      onClick={openEntityProfileDrawer}
                    >
                      {t('organisation.guide-receipts-step0-cta')}
                    </ScopeButton>
                  )}
                </BeforeYouScanStepContent>
              </li>
              <li
                style={{
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
                aria-hidden
              >
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#E0E0E0',
                    marginLeft: '13px',
                  }}
                />
              </li>
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 0',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary, #41aaaf)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  2
                </div>
                <BeforeYouScanStepContent>
                  <BeforeYouScanStepLabel>
                    {t('organisation.before-you-scan-select-tax-rates')}
                  </BeforeYouScanStepLabel>
                  {taxRatesStepCompleted ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'var(--color-primary, #41aaaf)',
                      }}
                    >
                      <CheckCircleOutlined style={{ fontSize: 16 }} />
                      {t('common.done')}
                    </span>
                  ) : (
                    <ScopeButton
                      type="dark-orange"
                      width="100px"
                      height="32px"
                      onClick={openTaxRatesDrawer}
                    >
                      {t('common.select')}
                    </ScopeButton>
                  )}
                </BeforeYouScanStepContent>
              </li>
              <li
                style={{
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
                aria-hidden
              >
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#E0E0E0',
                    marginLeft: '13px',
                  }}
                />
              </li>
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 0',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary, #41aaaf)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  3
                </div>
                <BeforeYouScanStepContent>
                  <BeforeYouScanStepLabel
                    as="span"
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    {t('organisation.before-you-scan-select-coa')}
                    <QuestionMarkTooltip
                      title={t('organisation.before-you-scan-coa-info')}
                    />
                  </BeforeYouScanStepLabel>
                  {coaStepCompleted ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 16,
                        fontWeight: 600,
                        color: 'var(--color-primary, #41aaaf)',
                      }}
                    >
                      <CheckCircleOutlined style={{ fontSize: 16 }} />
                      {t('common.done')}
                    </span>
                  ) : (
                    <ScopeButton
                      type="dark-orange"
                      width="100px"
                      height="32px"
                      onClick={openChartOfAccountsDrawer}
                    >
                      {t('common.select')}
                    </ScopeButton>
                  )}
                </BeforeYouScanStepContent>
              </li>
              <li
                style={{
                  padding: 0,
                  margin: 0,
                  border: 'none',
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
                aria-hidden
              >
                <div
                  style={{
                    width: '2px',
                    height: '24px',
                    backgroundColor: '#E0E0E0',
                    marginLeft: '13px',
                  }}
                />
              </li>
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 0',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary, #41aaaf)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  4
                </div>
                <BeforeYouScanStepContent>
                  <BeforeYouScanStepLabel
                    style={{
                      color:
                        hasTaxRates && coaStepCompleted && profileStepCompleted
                          ? 'var(--color-text-dark, #042a2a)'
                          : 'var(--color-text-muted, #5a6a6a)',
                    }}
                  >
                    {t('organisation.before-you-scan-upload-first-receipt')}
                  </BeforeYouScanStepLabel>
                  <ScopeButton
                    type="dark-orange"
                    width="100px"
                    height="32px"
                    onClick={triggerScanFileInput}
                    disabled={
                      !hasTaxRates || !coaStepCompleted || !profileStepCompleted
                    }
                  >
                    {t('common.upload')}
                  </ScopeButton>
                </BeforeYouScanStepContent>
              </li>
            </ul>
          </BeforeYouScanSteps>
          <BeforeYouScanIllustration alt="" src={ScanTransactionsImage} />
        </BeforeYouScanLayout>
      </StyledCard>
    );
  };

  const getLastUploadMetaSummary = () => {
    const excelFiles = (organisation?.excelFilesMetadata ?? []).slice();
    excelFiles.sort(
      (a, b) =>
        new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
    );
    let lastFile = excelFiles[0];
    // For receipts, fall back to currentStatus.files when excelFilesMetadata is empty (e.g. before metadata refresh)
    if (isReceipts && !lastFile && currentStatus?.files?.length) {
      const sorted = [...currentStatus.files].sort(
        (a, b) =>
          new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
      );
      lastFile = sorted[0] as unknown as typeof lastFile;
    }
    const transactions =
      allExcelTransactionsPage?.organisationId === organisationId
        ? allExcelTransactionsPage?.transactions ?? []
        : [];
    // Support transactionDate and alternate API shapes (e.g. date, transaction_date for receipts)
    const getTxDate = (tx: {
      transactionDate?: string | number;
      date?: string | number;
      transaction_date?: string | number;
    }): string | undefined => {
      const raw =
        tx.transactionDate ??
        (tx as { date?: string | number }).date ??
        (tx as { transaction_date?: string | number }).transaction_date;
      if (raw === null || raw === undefined || raw === '') {
        return undefined;
      }
      return String(raw);
    };
    const mostRecentTxDate =
      transactions.length > 0
        ? transactions.reduce<string | null>((max, tx) => {
            const d = getTxDate(tx);
            if (!d) return max;
            return !max || d > max ? d : max;
          }, null) ??
          lastFile?.uploadDate ??
          null
        : // For receipts with no transactions yet (e.g. file still processing), show last file upload date
        isReceipts && lastFile?.uploadDate
        ? lastFile.uploadDate
        : null;

    return { lastFile, mostRecentTxDate };
  };

  const renderPwaCaptureHeroMeta = () => {
    const { lastFile, mostRecentTxDate } = getLastUploadMetaSummary();
    const emptyPlaceholder = '-';
    const fileName = lastFile?.fileName ?? emptyPlaceholder;
    const txCount = lastFile ? String(lastFile.totalRows) : emptyPlaceholder;
    const uploadedOn = lastFile
      ? formatDisplayDate(lastFile.uploadDate, 'DD/MM/YYYY', i18n.language)
      : emptyPlaceholder;
    const uploadedBy = lastFile?.user?.email ?? emptyPlaceholder;
    const recentTx = mostRecentTxDate
      ? formatDisplayDate(mostRecentTxDate, 'DD/MM/YYYY', i18n.language)
      : emptyPlaceholder;

    return (
      <div
        className="scope-pwa-capture-hero__meta"
        aria-label={t('organisation.last-uploaded-file')}
      >
        <div className="scope-pwa-capture-hero__meta-file">
          <FileOutlined
            className="scope-pwa-capture-hero__meta-icon"
            aria-hidden
          />
          <div className="scope-pwa-capture-hero__meta-file-text">
            <span className="scope-pwa-capture-hero__meta-label">
              {t('organisation.last-uploaded-file')}
            </span>
            <Tooltip title={lastFile ? fileName : undefined}>
              <span className="scope-pwa-capture-hero__meta-file-name">
                {fileName}
              </span>
            </Tooltip>
          </div>
        </div>
        <div className="scope-pwa-capture-hero__meta-grid">
          <div
            className="scope-pwa-capture-hero__meta-cell"
            title={t('organisation.transactions')}
          >
            <UnorderedListOutlined
              className="scope-pwa-capture-hero__meta-icon"
              aria-hidden
            />
            <div className="scope-pwa-capture-hero__meta-cell-text">
              <span className="scope-pwa-capture-hero__meta-label">
                {t('organisation.transactions')}
              </span>
              <span className="scope-pwa-capture-hero__meta-value">{txCount}</span>
            </div>
          </div>
          <div
            className="scope-pwa-capture-hero__meta-cell"
            title={t('organisation.uploaded-on')}
          >
            <CalendarOutlined
              className="scope-pwa-capture-hero__meta-icon"
              aria-hidden
            />
            <div className="scope-pwa-capture-hero__meta-cell-text">
              <span className="scope-pwa-capture-hero__meta-label">
                {t('organisation.uploaded-on')}
              </span>
              <span className="scope-pwa-capture-hero__meta-value">
                {uploadedOn}
              </span>
            </div>
          </div>
          <div
            className="scope-pwa-capture-hero__meta-cell"
            title={t('organisation.uploaded-by')}
          >
            <UserOutlined
              className="scope-pwa-capture-hero__meta-icon"
              aria-hidden
            />
            <div className="scope-pwa-capture-hero__meta-cell-text">
              <span className="scope-pwa-capture-hero__meta-label">
                {t('organisation.uploaded-by')}
              </span>
              <Tooltip title={lastFile?.user?.email ? uploadedBy : undefined}>
                <span className="scope-pwa-capture-hero__meta-value scope-pwa-capture-hero__meta-value--truncate">
                  {uploadedBy}
                </span>
              </Tooltip>
            </div>
          </div>
          <div
            className="scope-pwa-capture-hero__meta-cell"
            title={t('organisation.most-recent-transaction-date')}
          >
            <FieldTimeOutlined
              className="scope-pwa-capture-hero__meta-icon"
              aria-hidden
            />
            <div className="scope-pwa-capture-hero__meta-cell-text">
              <span className="scope-pwa-capture-hero__meta-label">
                {t('organisation.most-recent-transaction-date')}
              </span>
              <span className="scope-pwa-capture-hero__meta-value">
                {recentTx}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUploadDocumentsMeta = (options?: {
    className?: string;
    forcePwaLayout?: boolean;
  }) => {
    const usePwaLayout = options?.forcePwaLayout || pwaMobileNav;
    const { lastFile, mostRecentTxDate } = getLastUploadMetaSummary();
    // Always show labels for both Excel and Receipts. When no files, show "-" placeholders so layout is consistent.
    const emptyPlaceholder = '-';
    const className = [
      usePwaLayout ? 'scope-pwa-upload-documents-meta' : undefined,
      options?.className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={className || undefined}
        style={
          usePwaLayout
            ? { width: '100%', marginBottom: 0 }
            : {
                display: 'flex',
                flexDirection: 'row',
                alignSelf: 'flex-start',
                flexWrap: 'wrap',
                gap: 20,
                columnGap: 50,
                marginBottom: '16px',
                width: '100%',
              }
        }
      >
        <CardInfo
          label={t('organisation.last-uploaded-file')}
          data={
            lastFile ? (
              <Tooltip title={lastFile.fileName}>
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: usePwaLayout ? 'normal' : 'nowrap',
                    maxWidth: usePwaLayout ? '100%' : 250,
                    minWidth: 0,
                    wordBreak: usePwaLayout ? 'break-word' : undefined,
                  }}
                >
                  {lastFile.fileName}
                </span>
              </Tooltip>
            ) : (
              emptyPlaceholder
            )
          }
          haveBorder
          truncate={!usePwaLayout}
          labelDataGap={usePwaLayout ? 6 : 0}
          dataBreakWord={usePwaLayout}
          style={usePwaLayout ? undefined : { minWidth: 260, maxWidth: 280 }}
        />
        <CardInfo
          label={t('organisation.transactions')}
          data={lastFile ? String(lastFile.totalRows) : emptyPlaceholder}
          haveBorder
          labelDataGap={usePwaLayout ? 6 : 0}
          style={usePwaLayout ? undefined : { minWidth: 110, maxWidth: 130 }}
        />
        <CardInfo
          label={t('organisation.uploaded-on')}
          data={
            lastFile
              ? formatDisplayDate(
                  lastFile.uploadDate,
                  'DD/MM/YYYY',
                  i18n.language
                )
              : emptyPlaceholder
          }
          haveBorder
          labelDataGap={usePwaLayout ? 6 : 0}
          style={usePwaLayout ? undefined : { minWidth: 140, maxWidth: 170 }}
        />
        <CardInfo
          label={t('organisation.uploaded-by')}
          data={lastFile?.user?.email ?? emptyPlaceholder}
          haveBorder
          labelDataGap={usePwaLayout ? 6 : 0}
          dataBreakWord={usePwaLayout}
          style={usePwaLayout ? undefined : { minWidth: 200, maxWidth: 280 }}
        />
        <CardInfo
          label={t('organisation.most-recent-transaction-date')}
          data={
            mostRecentTxDate
              ? formatDisplayDate(
                  mostRecentTxDate,
                  'DD/MM/YYYY',
                  i18n.language
                )
              : emptyPlaceholder
          }
          haveBorder
          labelDataGap={usePwaLayout ? 6 : 0}
        />
      </div>
    );
  };

  const renderPwaCaptureHeroActions = (options?: { includeMeta?: boolean }) => {
    if (!organisationId) return null;

    const includeMeta = options?.includeMeta !== false;
    const prefsLabel = t('organisation.scanning-preferences.title');
    const rulesLabel = t('organisation.extraction-rules.drawer-title');

    return (
      <>
        <div className="scope-pwa-capture-hero__actions">
          <DriveUploadHistory
            connectionId={organisationId}
            variant="circle"
            disabled={!scanAreaEnabled}
          />
          {onOpenInterpretationRules ? (
            <button
              type="button"
              className="scope-pwa-capture-hero__action"
              aria-label={rulesLabel}
              disabled={!scanAreaEnabled}
              onClick={() => {
                if (!scanAreaEnabled) return;
                onOpenInterpretationRules();
              }}
            >
              <span className="scope-pwa-capture-hero__action-orb" aria-hidden>
                <IconFuzzyMapping />
              </span>
            </button>
          ) : null}
          <button
            type="button"
            className={`scope-pwa-capture-hero__action${
              scanningPrefsDrawerOpen
                ? ' scope-pwa-capture-hero__action--active'
                : ''
            }`}
            aria-label={prefsLabel}
            aria-expanded={scanningPrefsDrawerOpen}
            disabled={scanningPreferencesDisabled}
            onClick={() => {
              if (scanningPreferencesDisabled) return;
              if (scanningPrefsDrawerOpen) {
                dispatch(turnDrawerOff());
                return;
              }
              openScanningPreferencesDrawer();
            }}
          >
            <span className="scope-pwa-capture-hero__action-orb" aria-hidden>
              <SettingOutlined />
            </span>
          </button>
        </div>
        {includeMeta ? renderPwaCaptureHeroMeta() : null}
      </>
    );
  };

  const renderPwaCaptureShutter = () => (
    <button
      type="button"
      className="scope-pwa-capture-hero__shutter"
      aria-label={
        canUploadNow
          ? t('organisation.receipts-tap-to-capture-prompt')
          : !scanAreaEnabled
          ? t('organisation.receipts-complete-steps')
          : t('organisation.receipts-wait-for-batch')
      }
      disabled={!canUploadNow}
      onClick={() => {
        if (!canUploadNow) return;
        openWebcamOrFilePicker();
      }}
    >
      <AiScanningLogo size={92} aria-hidden />
    </button>
  );

  const renderPwaCaptureHero = () => {
    const { lastFile } = getLastUploadMetaSummary();
    const entityName =
      resolvedOrganisation?.name ||
      resolvedOrganisation?.legalName ||
      resolvedOrganisation?.alternateName ||
      '';

    return (
      <>
        <input
          id="scan-files-input"
          type="file"
          accept={RECEIPT_UPLOAD_ACCEPT_WITH_CAMERA}
          multiple
          style={{ display: 'none' }}
          onChange={onFileInputChange}
        />
        <WebcamCaptureModal
          open={webcamOpen}
          onClose={() => setWebcamOpen(false)}
          onCapture={(file) => {
            if (canUploadNow) {
              addScanFiles([file]);
            }
          }}
        />
        {organisationId ? (
          <PwaCapturePage
            connectionId={organisationId}
            entityName={entityName}
            canUploadNow={canUploadNow}
            scanAreaEnabled={scanAreaEnabled}
            scanningPreferencesDisabled={scanningPreferencesDisabled}
            onTakePhoto={() => {
              if (!canUploadNow) return;
              openWebcamOrFilePicker();
            }}
            onUpload={() => {
              if (!canUploadNow) return;
              triggerScanFileInput();
            }}
            lastFile={lastFile ?? null}
            groupByTaxCode={groupByTaxCode}
            onGroupByTaxCodeChange={(checked) => {
              setGroupByTaxCode(checked);
              if (groupByTaxCodePrefKey && currentUser?.userUuid) {
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  { name: groupByTaxCodePrefKey, value: checked },
                  currentUser.userUuid
                );
              }
            }}
            mapItems={mapItems}
            onMapItemsChange={(checked) => {
              setMapItems(checked);
              if (mapItemsPrefKey && currentUser?.userUuid) {
                preferencesHook(
                  PreferenceActions.SET,
                  PreferenceSections.TRANSACTIONS,
                  { name: mapItemsPrefKey, value: checked },
                  currentUser.userUuid
                );
              }
            }}
            supportsMapItems={supportsMapItems}
            multiCurrencyMode={multiCurrencyMode}
            onMultiCurrencyModeChange={(mode) =>
              persistMultiCurrencyMode(mode, { notify: true })
            }
            supportsStopUploadMultiCurrency={supportsStopUploadMultiCurrency}
          />
        ) : null}
      </>
    );
  };

  const renderUploadDocumentsHeaderActions = () => {
    if (!isReceipts || !organisationId) return null;
    return (
      <div
        className={
          pwaMobileNav ? 'scope-pwa-upload-documents-actions' : undefined
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: pwaMobileNav ? 'stretch' : 'flex-end',
          flexShrink: 0,
          ...(pwaMobileNav ? { width: '100%' } : {}),
        }}
      >
        <DriveUploadHistory
          connectionId={organisationId}
          variant="button"
          buttonLabel={t('organisation.capture-log-button')}
          disabled={!scanAreaEnabled}
        />
        {onOpenInterpretationRules ? (
          <EntityTransactionsToolbarIconButton
            type="default"
            icon={
              <IconFuzzyMapping
                style={{
                  width: 14,
                  height: 14,
                  color: ENTITY_TOOLBAR_MUTED_ICON_COLOR,
                }}
              />
            }
            style={entityTransactionsToolbarButtonStyle(false)}
            onClick={onOpenInterpretationRules}
            disabled={!scanAreaEnabled}
          >
            {t('organisation.extraction-rules.drawer-title')}
          </EntityTransactionsToolbarIconButton>
        ) : null}
        {!pwaMobileNav ? (
          <CaptureStatisticsHistory
            connectionId={organisationId}
            organisationId={organisationId}
            variant="button"
            buttonLabel={t('users.history')}
            disabled={!scanAreaEnabled}
          />
        ) : null}
        {uploadDocumentsTopRightExtras}
      </div>
    );
  };

  const renderReceiptsBody = () => {
    const pwaCaptureHeroEmpty =
      pwaMobileNav &&
      isReceipts &&
      scanFiles.length === 0 &&
      !(currentStatus?.files?.length);
    const hasPostCaptureFiles =
      scanFiles.length > 0 || (currentStatus?.files?.length ?? 0) > 0;

    if (pwaCaptureHeroEmpty) {
      return (
        <div className="scope-pwa-upload-documents-body scope-pwa-upload-documents-body--hero">
          {renderPwaCaptureHero()}
        </div>
      );
    }

    const body = (
      <>
        {!(pwaMobileNav && hasPostCaptureFiles) ? (
          <>
            <div
              className={
                pwaMobileNav ? 'scope-pwa-upload-documents-title' : undefined
              }
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
                marginBottom: pwaMobileNav ? '12px' : '48px',
                width: '100%',
              }}
            >
              <CardTitleLargeSpan>
                {isReceipts ? (
                  <Space align="start" size={8}>
                    <IconBetaStar
                      style={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                        color: 'var(--color-primary, #41aaaf)',
                      }}
                    />
                    {t('organisation.scan-new-transactions')}
                    <Tooltip title={t('organisation.receipts-beta-tooltip')}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--color-primary, #41aaaf)',
                          backgroundColor: COLORS.successBg,
                          padding: '2px 6px',
                          borderRadius: 4,
                          border: '1px solid var(--color-border, #dbeae3)',
                          cursor: 'help',
                        }}
                      >
                        <IconBetaStar
                          style={{ width: 14, height: 14, flexShrink: 0 }}
                        />
                        {t('common.beta')}
                      </span>
                    </Tooltip>
                  </Space>
                ) : (
                  t('organisation.upload-new-transactions')
                )}
              </CardTitleLargeSpan>
              {!pwaMobileNav ? renderUploadDocumentsHeaderActions() : null}
            </div>

            {pwaMobileNav ? (
              <div className="scope-pwa-upload-documents-actions-block">
                {renderUploadDocumentsHeaderActions()}
              </div>
            ) : null}

            {renderUploadDocumentsMeta()}

            {/* Always show divider below labels so empty state matches layout when we have data */}
            <div
              className={
                pwaMobileNav ? 'scope-pwa-upload-documents-divider' : undefined
              }
              style={{
                borderTop: '1px solid var(--color-border, rgb(219, 234, 227))',
                margin: pwaMobileNav ? '16px 0' : '40px 0',
              }}
            />
          </>
        ) : null}

        {!isReceipts ? (
          <div>
            <CardTitleLargeSpan
              style={{
                display: 'block',
                marginBottom: '12px',
                fontSize: '18px',
                fontWeight: 400,
                color: 'var(--color-text-dark, #042a2a)',
              }}
            >
              {t('organisation.next-upload')}
            </CardTitleLargeSpan>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 35,
              }}
            >
              <CardDescSpan
                style={{
                  flex: 1,
                  minWidth: 0,
                  color: 'var(--color-text-muted, #5a6a6a)',
                  fontSize: '14px',
                }}
              >
                {t('organisation.excel-upload-intro-before-link')}
                <a
                  href={EXCEL_TEMPLATE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary, #41aaaf)', textDecoration: 'underline' }}
                >
                  {t('organisation.excel-upload-intro-link')}
                </a>
                {t('organisation.excel-upload-intro-after-link')}
                <br />
                <br />
                {t('organisation.excel-upload-support-article-before')}{' '}
                <a
                  href={SUPPORT_ARTICLE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary, #41aaaf)', textDecoration: 'underline' }}
                >
                  {t('organisation.support-article')}
                </a>{' '}
                {t('organisation.excel-upload-support-article-after')}
              </CardDescSpan>
              <img
                alt={t('organisation.upload-transactions-alt')}
                src={UploadTransactionsImage}
                style={{ width: 253, flexShrink: 0 }}
              />
            </div>
          </div>
        ) : hasPostCaptureFiles ? null : (
          <CardDescSpan
            className={
              pwaMobileNav ? 'scope-pwa-upload-documents-intro' : undefined
            }
            style={{
              marginBottom: '24px',
              color: 'var(--color-text-muted, #5a6a6a)',
              fontSize: '14px',
            }}
          >
            {t(
              pwaMobileNav
                ? 'organisation.receipts-upload-intro-pwa'
                : 'organisation.receipts-upload-intro'
            )}
          </CardDescSpan>
        )}

        {isReceipts && (
          <>
            <div
              className={
                pwaMobileNav ? 'scope-pwa-upload-documents-upload' : undefined
              }
            >
            <input
              id="scan-files-input"
              type="file"
              accept={
                pwaMobileNav
                  ? RECEIPT_UPLOAD_ACCEPT_WITH_CAMERA
                  : RECEIPT_UPLOAD_ACCEPT
              }
              multiple
              style={{ display: 'none' }}
              onChange={onFileInputChange}
            />
            <WebcamCaptureModal
              open={webcamOpen}
              onClose={() => setWebcamOpen(false)}
              onCapture={(file) => {
                if (canUploadNow) {
                  addScanFiles([file]);
                }
              }}
            />
            {scanFiles.length === 0 && !currentStatus?.files?.length ? (
              <>
                <ReceiptUploadLayout $matchColumnHeights={!multiCurrencyOpen}>
                  <ReceiptDropZone
                    ref={receiptsTourStep2Ref}
                    $canUpload={canUploadNow}
                    $isDragOver={isDragOver}
                    $pwa={pwaMobileNav}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => {
                      if (canUploadNow) {
                        document.getElementById('scan-files-input')?.click();
                      }
                    }}
                  >
                    <div style={{ marginBottom: '16px', opacity: 0.55 }}>
                      <AiScanningLogo size={80} />
                    </div>
                    <CardDescSpan
                      style={{ color: 'var(--color-text-base, #2e2e2e)', fontSize: '14px' }}
                    >
                      {canUploadNow
                        ? t(
                            pwaMobileNav
                              ? 'organisation.receipts-tap-to-capture-prompt'
                              : 'organisation.receipts-drag-drop-prompt'
                          )
                        : !scanAreaEnabled
                        ? t('organisation.receipts-complete-steps')
                        : t('organisation.receipts-wait-for-batch')}
                    </CardDescSpan>
                    {(canUploadNow || scanAreaEnabled) && (
                      <CardDescSpan
                        style={{
                          display: 'block',
                          marginTop: '4px',
                          color: 'var(--color-text-muted, #5a6a6a)',
                          fontSize: '12px',
                        }}
                      >
                        {canUploadNow
                          ? t('organisation.receipts-files-per-batch', {
                              count: MAX_SCAN_FILES,
                            })
                          : t('organisation.receipts-upload-available')}
                      </CardDescSpan>
                    )}
                    {canUploadNow && pwaMobileNav ? (
                      <ScopeButton
                        type="default"
                        icon={<CameraOutlined />}
                        className="scope-webcam-capture__dropzone-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          openWebcamOrFilePicker();
                        }}
                        style={{ marginTop: 12 }}
                      >
                        {t('organisation.receipts-take-photo')}
                      </ScopeButton>
                    ) : null}
                  </ReceiptDropZone>

                  {renderScanningPreferences(scanningPreferencesDisabled)}
                </ReceiptUploadLayout>
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    fontSize: 11,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    marginBottom: 12,
                  }}
                >
                  {t('organisation.receipts-ai-disclaimer')}
                </span>
              </>
            ) : (
              <>
                {(() => {
                  const hasFiles =
                    (currentStatus?.files?.length ?? 0) > 0 ||
                    scanFiles.length > 0;
                  const displayedFileCount =
                    currentStatus?.files?.length || scanFiles.length;
                  const isUploadComplete = hasFiles;
                  const isExtractComplete = currentStatus?.files?.length
                    ? currentStatus.files.every(
                        (f: ReceiptUploadStatusFile) =>
                          !isUploadStatusProcessing(f.status)
                      )
                    : scanFiles.every(
                        (f) => f.stage === 'done' || f.stage === 'error'
                      );
                  const isDoneComplete = isExtractComplete;
                  const step2Active = isUploadComplete && !isExtractComplete;
                  const canDismiss =
                    !!currentStatus?.files?.length &&
                    currentStatus.files.every(
                      (f: ReceiptUploadStatusFile) =>
                        !isUploadStatusProcessing(f.status)
                    );

                  const getStepState = (
                    step: 1 | 2 | 3
                  ): 'pending' | 'active' | 'complete' => {
                    if (step === 1) {
                      return isUploadComplete ? 'complete' : 'active';
                    }
                    if (step === 2) {
                      if (isExtractComplete) return 'complete';
                      if (step2Active) return 'active';
                      return 'pending';
                    }
                    if (isDoneComplete) return 'complete';
                    if (step2Active) return 'pending';
                    return 'pending';
                  };

                  const renderStepIndicator = (step: 1 | 2 | 3) => {
                    const state = getStepState(step);
                    return (
                      <StepIndicator $state={state} $pwa={pwaMobileNav}>
                        {renderStepIndicatorContent(state, step)}
                      </StepIndicator>
                    );
                  };

                  const stepKeys = [
                    'organisation.guide-receipts-step1-title',
                    'organisation.receipts-uploaded-files.step-extract',
                    'organisation.receipts-uploaded-files.step-done',
                  ] as const;

                  return (
                    <ReceiptUploadLayout
                      $matchColumnHeights={!multiCurrencyOpen && !pwaMobileNav}
                      $pwa={pwaMobileNav}
                    >
                      <UploadedFilesBox $pwa={pwaMobileNav}>
                        <UploadedFilesTitleRow $pwa={pwaMobileNav}>
                          <UploadedFilesTitle $pwa={pwaMobileNav}>
                            {t('organisation.receipts-uploaded-files.title')}
                          </UploadedFilesTitle>
                          {canDismiss && (
                            <Tooltip
                              title={t(
                                'organisation.receipts-clear-files-tooltip'
                              )}
                              placement="left"
                            >
                              <ExportButton
                                type="text"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dismissCompletedReceiptsUploadPanel();
                                }}
                                icon={
                                  <CloseOutlined
                                    style={{ width: 14, height: 14 }}
                                  />
                                }
                                aria-label={t(
                                  'organisation.receipts-clear-files-tooltip'
                                )}
                              />
                            </Tooltip>
                          )}
                        </UploadedFilesTitleRow>

                        {step2Active && (
                          <UploadedFilesProcessingHeader $pwa={pwaMobileNav}>
                            <UploadedFilesProcessingIcon $pwa={pwaMobileNav}>
                              <AiScanningLogo size={pwaMobileNav ? 16 : 22} />
                            </UploadedFilesProcessingIcon>
                            <div>
                              <UploadedFilesProcessingTitle $pwa={pwaMobileNav}>
                                {t(
                                  'organisation.receipts-uploaded-files.processing-title'
                                )}
                              </UploadedFilesProcessingTitle>
                              <UploadedFilesProcessingSubtitle
                                $pwa={pwaMobileNav}
                              >
                                {t(
                                  'organisation.receipts-uploaded-files.processing-subtitle'
                                )}
                              </UploadedFilesProcessingSubtitle>
                            </div>
                          </UploadedFilesProcessingHeader>
                        )}

                        <UploadedFilesStepsList $pwa={pwaMobileNav}>
                          {([1, 2, 3] as const).map((step) => {
                            const state = getStepState(step);
                            return (
                              <UploadedFilesStepRow
                                key={step}
                                $state={state}
                                $pwa={pwaMobileNav}
                              >
                                {renderStepIndicator(step)}
                                <span>{t(stepKeys[step - 1])}</span>
                              </UploadedFilesStepRow>
                            );
                          })}
                        </UploadedFilesStepsList>

                        {currentStatus?.files?.length ? (
                          <UploadedFilesList $pwa={pwaMobileNav}>
                            {currentStatus.files.map(
                              (file: ReceiptUploadStatusFile) => {
                                const { label, indicatorState } =
                                  getProgressFromUploadStatus(
                                    file.status,
                                    file.errorReason
                                  );
                                const isDone = indicatorState === 'complete';
                                const isError = indicatorState === 'error';
                                return (
                                  <UploadedFileRow
                                    key={file.importId}
                                    $pwa={pwaMobileNav}
                                  >
                                    {renderUploadedFileNameCell(
                                      file.fileName,
                                      file.importId
                                    )}
                                    <FileStatusCell $pwa={pwaMobileNav}>
                                      <StepIndicator
                                        $state={indicatorState}
                                        $pwa={pwaMobileNav}
                                      >
                                        {renderStepIndicatorContent(
                                          indicatorState
                                        )}
                                      </StepIndicator>
                                      <FileStatusLabel
                                        $done={isDone}
                                        $error={isError}
                                        $pwa={pwaMobileNav}
                                      >
                                        {label}
                                      </FileStatusLabel>
                                    </FileStatusCell>
                                  </UploadedFileRow>
                                );
                              }
                            )}
                          </UploadedFilesList>
                        ) : scanFiles.length > 0 ? (
                          <UploadedFilesList $pwa={pwaMobileNav}>
                            {scanFiles.map((item) => {
                              const indicatorState = getScanStageIndicatorState(
                                item.stage
                              );
                              const isDone = indicatorState === 'complete';
                              const isError = indicatorState === 'error';
                              return (
                                <UploadedFileRow
                                  key={item.id}
                                  $pwa={pwaMobileNav}
                                >
                                  {renderUploadedFileNameCell(item.name)}
                                  <FileStatusCell $pwa={pwaMobileNav}>
                                    <StepIndicator
                                      $state={indicatorState}
                                      $pwa={pwaMobileNav}
                                    >
                                      {renderStepIndicatorContent(
                                        indicatorState
                                      )}
                                    </StepIndicator>
                                    <FileStatusLabel
                                      $done={isDone}
                                      $error={isError}
                                      $pwa={pwaMobileNav}
                                    >
                                      {scanStageLabels[item.stage]}
                                    </FileStatusLabel>
                                  </FileStatusCell>
                                  {item.removable !== false ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeScanFile(item.id);
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color:
                                          'var(--color-text-muted, #5a6a6a)',
                                        cursor: 'pointer',
                                        padding: '0 4px',
                                        fontSize: '14px',
                                        flexShrink: 0,
                                      }}
                                      aria-label={`Remove ${item.name}`}
                                    >
                                      ×
                                    </button>
                                  ) : null}
                                </UploadedFileRow>
                              );
                            })}
                          </UploadedFilesList>
                        ) : null}

                        {(currentStatus?.files?.length ?? 0) > 0 &&
                          currentStatus.files.every(
                            (f: ReceiptUploadStatusFile) =>
                              getUploadFileIndicatorState(f.status) ===
                              'complete'
                          ) && (
                            <UploadedFilesSuccess $pwa={pwaMobileNav}>
                              <CheckCircleOutlined
                                style={{ fontSize: pwaMobileNav ? 14 : 18 }}
                              />
                              {t(
                                'organisation.receipts-uploaded-files.all-processed'
                              )}
                            </UploadedFilesSuccess>
                          )}

                        <UploadedFilesFooter $pwa={pwaMobileNav}>
                          <Space
                            wrap
                            direction={pwaMobileNav ? 'vertical' : 'horizontal'}
                            style={pwaMobileNav ? { width: '100%' } : undefined}
                            size={pwaMobileNav ? 6 : undefined}
                          >
                            {onGoToTransactionsTab && (
                              <ScopeButton
                                type="dark-orange"
                                width={pwaMobileNav ? '100%' : 'auto'}
                                height={pwaMobileNav ? '32px' : '35px'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onGoToTransactionsTab();
                                }}
                                style={{
                                  padding: '0 16px',
                                  fontSize: pwaMobileNav ? '12px' : '13px',
                                }}
                              >
                                {t('organisation.go-to-transactions-tab')}
                              </ScopeButton>
                            )}
                            {!pwaMobileNav ? (
                              <Tooltip
                                title={
                                  !canUploadNow
                                    ? t(
                                        'organisation.upload-available-after-batch'
                                      )
                                    : undefined
                                }
                              >
                                <span>
                                  <ExportButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (canUploadNow) {
                                        document
                                          .getElementById('scan-files-input')
                                          ?.click();
                                      }
                                    }}
                                    disabled={!canUploadNow}
                                  >
                                    {t('organisation.upload-more-files')}
                                  </ExportButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Space>
                          <CardDescSpan
                            style={{
                              fontSize: pwaMobileNav ? '11px' : '12px',
                              color: 'var(--color-text-muted, #5a6a6a)',
                            }}
                          >
                            {t(
                              displayedFileCount === 1
                                ? 'organisation.upload-file-count-singular'
                                : 'organisation.upload-file-count-plural',
                              {
                                count: displayedFileCount,
                                max: MAX_SCAN_FILES,
                              }
                            )}
                          </CardDescSpan>
                        </UploadedFilesFooter>
                      </UploadedFilesBox>
                      {pwaMobileNav ? (
                        <div className="scope-pwa-post-capture-actions">
                          <div className="scope-pwa-post-capture-shutter">
                            {renderPwaCaptureShutter()}
                          </div>
                          {renderPwaCaptureHeroActions({ includeMeta: true })}
                        </div>
                      ) : (
                        renderScanningPreferences(scanningPreferencesDisabled)
                      )}
                    </ReceiptUploadLayout>
                  );
                })()}
                <span
                  style={{
                    display: 'block',
                    width: '100%',
                    fontSize: 11,
                    color: 'var(--color-text-muted, #5a6a6a)',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    marginBottom: 12,
                  }}
                >
                  {t('organisation.receipts-ai-disclaimer')}
                </span>
              </>
            )}
            </div>

          </>
        )}

        {variant === 'receipts' && !pwaMobileNav && (
          <div
            ref={receiptsTourStep3Ref}
            className="scope-receipts-excel-import"
          >
            <CardTitleLargeSpan className="scope-receipts-excel-import__title">
              <img
                src={ExcelLogo}
                alt=""
                className="scope-receipts-excel-import__icon"
              />
              {t('organisation.import-other-transactions-title')}
            </CardTitleLargeSpan>
            <div className="scope-receipts-excel-import__row">
              <CardDescSpan className="scope-receipts-excel-import__copy">
                {t('organisation.receipts-import-excel-prompt')}
                <a
                  href={EXCEL_TEMPLATE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="scope-receipts-excel-import__link"
                >
                  {t('organisation.receipts-import-excel-template-link')}
                </a>
                .
              </CardDescSpan>
              <ScopeButton
                type="default"
                width="230px"
                height="40px"
                onClick={onUploadTransactions}
                icon={<ImportOutlined />}
                style={{ flexShrink: 0 }}
              >
                {t('organisation.import-transactions-title')}
              </ScopeButton>
            </div>
          </div>
        )}

        {!isReceipts && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <ScopeButton
              type="dark-orange"
              width="230px"
              height="40px"
              onClick={onUploadTransactions}
            >
              Upload Excel / CSV File
            </ScopeButton>
            <a
              href={SUPPORT_ARTICLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 20px',
                height: 40,
                border: '1px solid var(--color-primary, #41aaaf)',
                borderRadius: 50,
                backgroundColor: 'transparent',
                color: 'var(--color-primary, #41aaaf)',
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
                cursor: 'pointer',
              }}
            >
              {t('organisation.learn-more')}
            </a>
          </div>
        )}
      </>
    );

    if (pwaMobileNav && isReceipts) {
      return (
        <div className="scope-pwa-upload-documents-body">{body}</div>
      );
    }

    return body;
  };

  const pwaCaptureHeroEmpty =
    pwaMobileNav &&
    isReceipts &&
    scanFiles.length === 0 &&
    !(currentStatus?.files?.length);

  return (
    <>
      <div
        className={
          pwaMobileNav
            ? `scope-pwa-upload-documents-wrap${
                pwaCaptureHeroEmpty
                  ? ' scope-pwa-upload-documents-wrap--hero'
                  : ''
              }`
            : undefined
        }
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: pwaMobileNav ? undefined : 1250,
        }}
      >
        {renderChecklistCard()}

        <div ref={scanProgressCardRef}>
        <StyledCard
          $nopadding
          fullsize
          $borderless={pwaMobileNav}
          $stopshadowbox={pwaMobileNav}
          bg_color={pwaMobileNav ? 'transparent' : undefined}
          className={
            pwaMobileNav
              ? `scope-pwa-upload-documents-card${
                  pwaCaptureHeroEmpty
                    ? ' scope-pwa-upload-documents-card--hero'
                    : ''
                }`
              : undefined
          }
          style={{
            position: 'relative',
            boxShadow: pwaMobileNav || pwaCaptureHeroEmpty
              ? 'none'
              : '0px 4px 10px 0px #0000000D',
            marginBottom: pwaCaptureHeroEmpty ? 0 : '20px',
            display: 'flex',
            fontSize: 14,
            fontWeight: 400,
            padding: pwaMobileNav
              ? pwaCaptureHeroEmpty
                ? 0
                : '16px 0'
              : '24px 0',
            borderRadius: pwaCaptureHeroEmpty ? 0 : '8px',
            backgroundColor:
              pwaMobileNav || pwaCaptureHeroEmpty
                ? 'transparent'
                : 'var(--color-surface-raised, #ffffff)',
            background:
              pwaMobileNav || pwaCaptureHeroEmpty ? 'transparent' : undefined,
          }}
          width="100%"
        >
          <div
            className={
              pwaMobileNav ? 'scope-pwa-upload-documents-card__body' : undefined
            }
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: pwaMobileNav ? undefined : '0 24px 24px',
            }}
          >
            {renderReceiptsBody()}
          </div>
        </StyledCard>
        </div>
      </div>

      {!pwaMobileNav && isReceipts && (
        <ScopeTour
          open={receiptsTourOpen}
          onClose={handleReceiptsTourClose}
          steps={receiptsTourSteps}
        />
      )}

      {/* SSE subscription for live receipt status (list is shown in the card above) */}
      {isReceipts && batchId && !batchAllProcessed && !sessionUploadActive && (
        <SseNotifications
          id={batchId}
          setMessage={applySseMessage}
          type="receipts"
        />
      )}

      {uploadPanelAttachmentPreview && (
        <AttachmentViewer
          onClose={() => setUploadPanelAttachmentPreview(null)}
          transactionReference={uploadPanelAttachmentPreview.fileName}
          attachments={uploadPanelAttachmentPreview.attachments}
          attachmentLoading={uploadPanelAttachmentLoading}
          providerName={organisation?.providerName ?? ''}
          attachmentSourceUrl=""
          showUpload={false}
          standaloneWidth={720}
          standaloneFullHeight
          centered
        />
      )}
    </>
  );
};


